<template>
  <div class="rwc-admin">
    <v-container fluid class="pa-5">
      <!-- ════ HEADER ════ -->
      <v-row class="mb-5">
        <v-col cols="12">
          <div class="header-bar">
            <div class="header-bar-left">
              <div class="header-icon-wrap">
                <v-icon size="32" color="white">mdi-school</v-icon>
              </div>
              <div>
                <h1 class="header-title">จำนวนนักเรียน — ระบบ ศธ.02</h1>
                <p class="header-sub">
                  ข้อมูลนักเรียน นักศึกษา สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา
                </p>
              </div>
            </div>
            <div class="header-bar-right">
              <div class="header-stat-pill">
                <v-icon small color="rgba(255,255,255,.7)" class="mr-1"
                  >mdi-calendar</v-icon
                >
                <span>พ.ศ. {{ searchYear || currentYear }}</span>
              </div>
              <div class="header-stat-pill header-stat-pill--accent">
                <v-icon small class="mr-1">mdi-account-group</v-icon>
                <strong>{{ formatNumber(totalStudents) }}</strong
                >&nbsp;คน
              </div>
              <v-btn
                small
                dark
                color="rgba(255,255,255,.18)"
                class="ml-1 header-action-btn"
                @click="openCsvDialog"
              >
                <v-icon left small>mdi-file-delimited</v-icon>CSV
              </v-btn>
              <v-btn
                small
                dark
                color="rgba(255,255,255,.18)"
                class="header-action-btn"
                @click="openApiDialog"
              >
                <v-icon left small>mdi-cloud-download</v-icon>API ศธ.02
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- ════ KPI CARDS ════ -->
      <v-row class="mb-5">
        <v-col v-for="kpi in kpiCards" :key="kpi.key" cols="6" sm="3">
          <div class="kpi-card" :style="`--c:${kpi.color}`">
            <div class="kpi-top">
              <span class="kpi-label">{{ kpi.label }}</span>
              <v-avatar :color="kpi.color" size="40" class="kpi-avatar">
                <v-icon dark size="20">{{ kpi.icon }}</v-icon>
              </v-avatar>
            </div>
            <div class="kpi-value">{{ formatNumber(kpi.value) }}</div>
            <div class="kpi-sub">{{ kpi.sub }}</div>
            <v-progress-linear
              :value="kpi.pct"
              :color="kpi.color"
              height="3"
              rounded
              class="mt-2"
              background-color="rgba(0,0,0,.06)"
            />
          </div>
        </v-col>
      </v-row>

      <!-- ════ TABS ════ -->
      <v-row class="mb-3">
        <v-col cols="12">
          <div class="main-tabs-wrap">
            <v-tabs
              v-model="activeTab"
              background-color="white"
              color="primary"
              show-arrows
            >
              <v-tab
                ><v-icon small class="mr-2">mdi-view-dashboard</v-icon
                >ภาพรวม</v-tab
              >
              <v-tab>
                <v-icon small class="mr-2">mdi-podium</v-icon>วิเคราะห์สาขา
                (หลักสูตร 2567+)
                <v-chip x-small color="deep-purple" dark class="ml-2"
                  >ใหม่</v-chip
                >
              </v-tab>
            </v-tabs>
          </div>
        </v-col>
      </v-row>

      <v-tabs-items v-model="activeTab">
        <!-- ══════ TAB 0: ภาพรวม ══════ -->
        <v-tab-item>
          <!-- MAP + REGION -->
          <v-row class="mb-5">
            <v-col cols="12" md="6">
              <div class="panel h-100">
                <div class="panel-head">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-map-outline</v-icon
                  >
                  <span>ความหนาแน่นนักเรียนรายจังหวัด</span>
                  <v-spacer />
                  <v-btn-toggle
                    v-model="mapMode"
                    mandatory
                    dense
                    class="map-toggle"
                  >
                    <v-btn x-small value="students">นักเรียน</v-btn>
                    <v-btn x-small value="colleges">วิทยาลัย</v-btn>
                  </v-btn-toggle>
                </div>
                <div class="map-wrap" ref="mapWrap">
                  <div id="th-map" ref="thMap" class="th-map"></div>
                  <transition name="tt">
                    <div
                      v-if="mapTip.show"
                      class="map-tip"
                      :style="{ left: mapTip.x + 'px', top: mapTip.y + 'px' }"
                    >
                      <div class="map-tip-name">{{ mapTip.province }}</div>
                      <div class="map-tip-region">{{ mapTip.region }}</div>
                      <div class="map-tip-row">
                        <span>นักเรียน</span
                        ><strong>{{ formatNumber(mapTip.students) }} คน</strong>
                      </div>
                      <div class="map-tip-row">
                        <span>วิทยาลัย</span
                        ><strong>{{ mapTip.colleges }} แห่ง</strong>
                      </div>
                      <div class="map-tip-bar">
                        <div
                          :style="`width:${mapTip.pct}%`"
                          class="map-tip-bar-fill"
                        ></div>
                      </div>
                    </div>
                  </transition>
                  <div class="map-ctrl">
                    <button class="map-ctrl-btn" @click="mapZoomIn">
                      <v-icon size="16">mdi-plus</v-icon>
                    </button>
                    <button class="map-ctrl-btn" @click="mapZoomOut">
                      <v-icon size="16">mdi-minus</v-icon>
                    </button>
                    <button class="map-ctrl-btn" @click="mapFit">
                      <v-icon size="16">mdi-home-outline</v-icon>
                    </button>
                  </div>
                  <div class="map-legend">
                    <div class="map-legend-title">
                      {{
                        mapMode === "students"
                          ? "จำนวนนักเรียน"
                          : "จำนวนวิทยาลัย"
                      }}
                    </div>
                    <div class="map-legend-grad"></div>
                    <div class="map-legend-lbl">
                      <span>น้อย</span><span>มาก</span>
                    </div>
                  </div>
                  <transition name="tt">
                    <div v-if="selectedProvince" class="map-selected-info">
                      <strong>{{ selectedProvince.name }}</strong>
                      <span class="map-selected-val"
                        >{{ formatNumber(selectedProvince.students) }} คน</span
                      >
                      <v-btn
                        x-small
                        text
                        color="primary"
                        @click="filterByProvince(selectedProvince)"
                        >กรองข้อมูล</v-btn
                      >
                      <v-btn icon x-small @click="selectedProvince = null"
                        ><v-icon size="14">mdi-close</v-icon></v-btn
                      >
                    </div>
                  </transition>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="panel h-100">
                <div class="panel-head">
                  <v-icon small color="teal" class="mr-2"
                    >mdi-map-legend</v-icon
                  >
                  <span>บทสรุปนักเรียนรายภาค</span>
                  <v-spacer />
                  <v-chip x-small outlined color="teal"
                    >{{ regionSummary.length }} ภาค</v-chip
                  >
                </div>
                <div class="region-table-wrap">
                  <table class="region-tbl">
                    <thead>
                      <tr>
                        <th>ภาค</th>
                        <th class="text-right">นักเรียน</th>
                        <th class="text-right">วิทยาลัย</th>
                        <th class="text-right">สาขา</th>
                        <th style="width:80px">สัดส่วน</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in regionSummary"
                        :key="row.region"
                        class="region-row"
                        @click="
                          filterRegion = row.region;
                          showAdvanced = true;
                          onRegionChange();
                        "
                      >
                        <td>
                          <span
                            class="region-dot"
                            :style="`background:${regionColor(row.region)}`"
                          ></span>
                          {{ row.region }}
                        </td>
                        <td class="text-right mono font-weight-bold">
                          {{ formatNumber(row.students) }}
                        </td>
                        <td class="text-right mono">{{ row.colleges }}</td>
                        <td class="text-right mono">{{ row.courses }}</td>
                        <td>
                          <div class="region-bar-bg">
                            <div
                              class="region-bar-fill"
                              :style="
                                `width:${row.pct}%;background:${regionColor(
                                  row.region
                                )}`
                              "
                            ></div>
                          </div>
                          <span class="region-pct">{{ row.pct }}%</span>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="region-total">
                        <td>รวมทั้งหมด</td>
                        <td class="text-right mono">
                          {{ formatNumber(statistics.total) }}
                        </td>
                        <td class="text-right mono">
                          {{
                            new Set(
                              filteredData.map(i => i.rate_work_college_code)
                            ).size
                          }}
                        </td>
                        <td class="text-right mono">
                          {{
                            new Set(
                              filteredData
                                .map(i => i.name_type_course)
                                .filter(Boolean)
                            ).size
                          }}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- CHARTS ROW -->
          <v-row class="mb-5">
            <v-col cols="12" md="6">
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="panel h-100">
                    <div class="panel-head">
                      <v-icon small color="indigo" class="mr-2"
                        >mdi-chart-donut</v-icon
                      ><span>สัดส่วน ปวช./ปวส.</span>
                    </div>
                    <apexchart
                      type="donut"
                      height="240"
                      :options="donutOpts"
                      :series="donutSeries"
                    />
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="panel h-100">
                    <div class="panel-head">
                      <v-icon small color="teal" class="mr-2"
                        >mdi-chart-bar</v-icon
                      ><span>แยกชั้นปี</span>
                    </div>
                    <apexchart
                      type="bar"
                      height="240"
                      :options="levelBarOpts"
                      :series="levelBarSeries"
                    />
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="panel">
                    <div class="panel-head">
                      <v-icon small color="red darken-2" class="mr-2"
                        >mdi-chart-bar</v-icon
                      >
                      <span>10 สาขาวิชาที่มีนักเรียนน้อยที่สุด</span>
                      <v-spacer />
                      <v-chip
                        x-small
                        color="red lighten-4"
                        text-color="red darken-3"
                        >ต้องการการพัฒนา</v-chip
                      >
                    </div>
                    <apexchart
                      v-if="bottomCourseSeries[0].data.length"
                      type="bar"
                      height="200"
                      :options="bottomCourseOpts"
                      :series="bottomCourseSeries"
                    />
                    <div
                      v-else
                      class="d-flex align-center justify-center"
                      style="height:200px;color:#94a3b8;font-size:.85rem"
                    >
                      <v-icon color="grey lighten-1" class="mr-2"
                        >mdi-information-outline</v-icon
                      >ไม่พบข้อมูลสาขาวิชา
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6">
              <div class="panel h-100">
                <div class="panel-head">
                  <v-icon small color="deep-orange" class="mr-2"
                    >mdi-podium-gold</v-icon
                  >
                  <span>Top 10 สาขาวิชา (จำนวนนักเรียนมากที่สุด)</span>
                  <v-spacer />
                  <v-chip
                    x-small
                    color="blue lighten-4"
                    text-color="blue darken-3"
                    >อันดับ 1–10</v-chip
                  >
                </div>
                <apexchart
                  v-if="topCourseSeries[0] && topCourseSeries[0].data.length"
                  type="bar"
                  height="420"
                  :options="topCourseOpts"
                  :series="topCourseSeries"
                />
                <div
                  v-else
                  class="d-flex align-center justify-center"
                  style="height:420px;color:#94a3b8;font-size:.85rem"
                >
                  <v-icon color="grey lighten-1" class="mr-2"
                    >mdi-information-outline</v-icon
                  >ไม่พบข้อมูลสาขาวิชา
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- FILTER + TABLE -->
          <v-row>
            <v-col cols="12" md="3">
              <div class="panel filter-panel" style="position:sticky;top:16px">
                <div class="panel-head">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-filter-variant</v-icon
                  >
                  <span>ตัวกรองข้อมูล</span>
                  <v-spacer />
                  <v-chip
                    v-if="totalActiveFilters > 0"
                    x-small
                    color="primary"
                    dark
                    >{{ totalActiveFilters }}</v-chip
                  >
                </div>
                <div class="filter-body">
                  <div class="filter-group">
                    <div class="filter-group-label">ปีการศึกษา</div>
                    <v-select
                      v-model="searchYear"
                      :items="years"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      class="filter-field"
                      @change="onYearChange"
                    >
                      <template #item="{ item }">พ.ศ. {{ item }}</template>
                      <template #selection="{ item }">พ.ศ. {{ item }}</template>
                    </v-select>
                  </div>
                  <div class="filter-group">
                    <div class="filter-group-label">วิทยาลัย</div>
                    <v-autocomplete
                      v-model="collegeSearch"
                      :items="colleges"
                      item-text="college_name"
                      item-value="college_code"
                      outlined
                      dense
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-domain"
                      placeholder="ทั้งหมด"
                      class="filter-field"
                      @change="applyFilters"
                    >
                      <template #item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{
                            item.college_name
                          }}</v-list-item-title>
                          <v-list-item-subtitle class="caption">{{
                            item.college_code
                          }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </div>
                  <v-divider class="my-3" />
                  <div class="adv-toggle" @click="showAdvanced = !showAdvanced">
                    <span
                      ><v-icon small class="mr-1">mdi-tune</v-icon
                      >กรองขั้นสูง</span
                    >
                    <div class="d-flex align-center" style="gap:6px">
                      <v-chip
                        v-if="advFilterCount > 0"
                        x-small
                        color="primary"
                        dark
                        >{{ advFilterCount }}</v-chip
                      >
                      <v-icon small>{{
                        showAdvanced ? "mdi-chevron-up" : "mdi-chevron-down"
                      }}</v-icon>
                    </div>
                  </div>
                  <v-expand-transition>
                    <div v-show="showAdvanced" class="adv-body">
                      <div class="filter-group">
                        <div class="filter-group-label">ระดับชั้น</div>
                        <div class="level-chips">
                          <div
                            v-for="lv in levelOptions"
                            :key="lv.value"
                            class="level-chip"
                            :class="{
                              active: filterLevels.includes(lv.value),
                              [lv.cls]: true
                            }"
                            @click="toggleLevel(lv.value)"
                          >
                            {{ lv.text }}
                          </div>
                        </div>
                      </div>
                      <div class="filter-group">
                        <div class="filter-group-label">ภาค</div>
                        <v-select
                          v-model="filterRegion"
                          :items="regionOptions"
                          item-text="text"
                          item-value="value"
                          outlined
                          dense
                          hide-details
                          clearable
                          prepend-inner-icon="mdi-map-marker-radius"
                          placeholder="ทุกภาค"
                          class="filter-field"
                          @change="onRegionChange"
                        />
                      </div>
                      <div v-if="filterRegion" class="filter-group">
                        <div class="filter-group-label">จังหวัด</div>
                        <v-autocomplete
                          v-model="filterProvince"
                          :items="provinceOptions"
                          item-text="text"
                          item-value="value"
                          outlined
                          dense
                          hide-details
                          clearable
                          multiple
                          prepend-inner-icon="mdi-city"
                          placeholder="ทุกจังหวัด"
                          class="filter-field"
                          @change="applyFilters"
                        >
                          <template #selection="{ item, index }">
                            <v-chip
                              v-if="index < 1"
                              x-small
                              color="blue-grey"
                              dark
                              class="mr-1"
                              >{{ item.text }}</v-chip
                            >
                            <span v-if="index === 1" class="caption grey--text"
                              >+{{ filterProvince.length - 1 }}</span
                            >
                          </template>
                        </v-autocomplete>
                      </div>
                      <div class="filter-group">
                        <div class="filter-group-label">ประเภทวิชา</div>
                        <v-autocomplete
                          v-model="filterSubjectTypes"
                          :items="subjectTypeOptions"
                          item-text="subjectTypeNameTh"
                          item-value="subjectTypeID"
                          outlined
                          dense
                          hide-details
                          clearable
                          multiple
                          prepend-inner-icon="mdi-book-education"
                          placeholder="ทั้งหมด"
                          class="filter-field"
                          :loading="loadingCurriculum"
                          @change="onSubjectTypeChange"
                        >
                          <template #selection="{ item, index }">
                            <v-chip
                              v-if="index < 1"
                              x-small
                              color="indigo"
                              dark
                              class="mr-1 mb-1"
                            >
                              <span
                                class="text-truncate"
                                style="max-width:80px"
                                >{{ item.subjectTypeNameTh }}</span
                              >
                            </v-chip>
                            <span v-if="index === 1" class="caption grey--text"
                              >+{{ filterSubjectTypes.length - 1 }}</span
                            >
                          </template>
                        </v-autocomplete>
                      </div>
                      <div class="filter-group">
                        <div class="filter-group-label">สาขาวิชา</div>
                        <v-autocomplete
                          v-model="filterMajors"
                          :items="majorOptions"
                          item-text="majorNameTh"
                          item-value="majorID"
                          outlined
                          dense
                          hide-details
                          clearable
                          multiple
                          prepend-inner-icon="mdi-certificate"
                          placeholder="ทั้งหมด"
                          class="filter-field"
                          :loading="loadingCurriculum"
                          :disabled="majorOptions.length === 0"
                          @change="applyFilters"
                        >
                          <template #selection="{ item, index }">
                            <v-chip
                              v-if="index < 1"
                              x-small
                              color="teal"
                              dark
                              class="mr-1 mb-1"
                            >
                              <span
                                class="text-truncate"
                                style="max-width:80px"
                                >{{ item.majorNameTh }}</span
                              >
                            </v-chip>
                            <span v-if="index === 1" class="caption grey--text"
                              >+{{ filterMajors.length - 1 }}</span
                            >
                          </template>
                        </v-autocomplete>
                      </div>
                      <div class="filter-group">
                        <div class="filter-group-label">
                          จำนวนนักเรียน (รวม): {{ studentRangeLabel }}
                        </div>
                        <v-range-slider
                          v-model="filterStudentRange"
                          :min="0"
                          :max="studentRangeMax"
                          :step="studentRangeStep"
                          color="primary"
                          track-color="grey lighten-2"
                          thumb-label="always"
                          class="mt-6 px-2"
                          @end="applyFilters"
                        >
                          <template #thumb-label="{ value }"
                            >{{ Math.round(value / 1000) }}K</template
                          >
                        </v-range-slider>
                      </div>
                      <v-btn
                        v-if="advFilterCount > 0"
                        block
                        small
                        text
                        color="error"
                        class="mt-1 mb-2"
                        @click="clearAdvancedFilters"
                      >
                        <v-icon left small>mdi-filter-remove</v-icon
                        >ล้างตัวกรองขั้นสูง ({{ advFilterCount }})
                      </v-btn>
                    </div>
                  </v-expand-transition>
                  <v-divider class="my-3" />
                  <div class="summary-mini">
                    <div class="summary-mini-title">
                      <v-icon small color="primary" class="mr-1"
                        >mdi-sigma</v-icon
                      >สรุปตามระดับชั้น
                    </div>
                    <table class="summary-tbl">
                      <tr
                        v-for="row in summaryRows"
                        :key="row.label"
                        :class="row.cls"
                      >
                        <td>{{ row.label }}</td>
                        <td class="text-right mono">
                          {{ formatNumber(row.val) }}
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="9">
              <div class="panel">
                <div class="panel-head flex-wrap" style="gap:8px">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-table-search</v-icon
                  >
                  <span class="mr-2">รายละเอียดข้อมูลนักเรียน นักศึกษา</span>
                  <template v-if="totalActiveFilters > 0">
                    <v-chip
                      v-for="lv in filterLevels"
                      :key="'lv' + lv"
                      x-small
                      close
                      class="mr-1"
                      :color="lv === '20' ? 'blue' : 'purple'"
                      dark
                      @click:close="removeLevel(lv)"
                    >
                      {{ lv === "20" ? "ปวช." : "ปวส." }}
                    </v-chip>
                    <v-chip
                      v-if="filterRegion"
                      x-small
                      close
                      class="mr-1"
                      color="blue-grey"
                      dark
                      @click:close="
                        filterRegion = null;
                        filterProvince = [];
                        applyFilters();
                      "
                      >{{ filterRegion }}</v-chip
                    >
                    <v-chip
                      v-for="p in filterProvince"
                      :key="'p' + p"
                      x-small
                      close
                      class="mr-1"
                      color="blue-grey lighten-1"
                      dark
                      @click:close="removeProvince(p)"
                      >{{ p }}</v-chip
                    >
                    <v-chip
                      v-for="st in filterSubjectTypes"
                      :key="'st' + st"
                      x-small
                      close
                      class="mr-1"
                      color="indigo"
                      dark
                      @click:close="removeSubjectType(st)"
                      >{{ getSubjectTypeName(st) }}</v-chip
                    >
                    <v-chip
                      v-for="mj in filterMajors"
                      :key="'mj' + mj"
                      x-small
                      close
                      class="mr-1"
                      color="teal"
                      dark
                      @click:close="removeMajor(mj)"
                      >{{ getMajorName(mj) }}</v-chip
                    >
                  </template>
                  <v-spacer />
                  <v-chip small outlined color="primary" class="mr-2"
                    >{{ filteredData.length }} รายการ</v-chip
                  >
                  <v-btn
                    small
                    color="success"
                    :disabled="!filteredData.length"
                    class="action-btn"
                    @click="exportToExcel"
                  >
                    <v-icon left small>mdi-microsoft-excel</v-icon>Excel
                  </v-btn>
                </div>
                <div class="px-4 pt-3 pb-2">
                  <v-text-field
                    v-model="search"
                    prepend-inner-icon="mdi-magnify"
                    label="ค้นหาข้อมูลในตาราง..."
                    outlined
                    dense
                    clearable
                    hide-details
                    class="search-field"
                  />
                </div>
                <v-data-table
                  :headers="headers"
                  :items="filteredData"
                  :search="search"
                  :loading="loading"
                  :items-per-page="15"
                  :footer-props="{
                    'items-per-page-options': [10, 15, 25, 50, -1],
                    'items-per-page-text': 'แสดง:'
                  }"
                  class="rwc-table"
                  loading-text="กำลังโหลดข้อมูล..."
                  no-data-text="ไม่พบข้อมูล"
                  no-results-text="ไม่พบผลลัพธ์"
                >
                  <template #item.rate_work_college_year="{ item }">
                    <v-chip x-small color="primary" dark>{{
                      item.rate_work_college_year
                    }}</v-chip>
                  </template>
                  <template #item.college_name="{ item }">
                    <div class="cell-college">
                      <strong>{{ item.college_name }}</strong>
                      <span v-if="item.province_name" class="cell-province">{{
                        item.province_name
                      }}</span>
                    </div>
                  </template>
                  <template #item.rate_work_college_id_class="{ item }">
                    <v-chip
                      x-small
                      dark
                      :color="
                        String(item.rate_work_college_id_class) === '20'
                          ? 'blue'
                          : 'purple'
                      "
                    >
                      {{
                        String(item.rate_work_college_id_class) === "20"
                          ? "ปวช."
                          : "ปวส."
                      }}
                    </v-chip>
                  </template>
                  <template #item.subject_typeName="{ item }">
                    <span class="cell-subject">{{
                      item.subject_typeName
                    }}</span>
                  </template>
                  <template #item.name_type_course="{ item }">
                    <v-tooltip bottom max-width="300">
                      <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="cell-course">{{
                          truncate(item.name_type_course, 28)
                        }}</span>
                      </template>
                      {{ item.name_type_course }}
                    </v-tooltip>
                  </template>
                  <template #item.rate_work_field_study_lavel_1="{ item }">
                    <span class="mono cell-num">{{
                      formatNumber(item.rate_work_field_study_lavel_1)
                    }}</span>
                  </template>
                  <template #item.rate_work_field_study_lavel_2="{ item }">
                    <span class="mono cell-num">{{
                      formatNumber(item.rate_work_field_study_lavel_2)
                    }}</span>
                  </template>
                  <template #item.rate_work_field_study_lavel_3="{ item }">
                    <span class="mono cell-num">{{
                      formatNumber(item.rate_work_field_study_lavel_3)
                    }}</span>
                  </template>
                  <template #item.total_row="{ item }">
                    <span
                      class="mono cell-num font-weight-bold primary--text"
                      >{{ formatNumber(item.total_row) }}</span
                    >
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- ══════ TAB 1: วิเคราะห์สาขา (หลักสูตร 2567+) ══════ -->
        <v-tab-item>
          <!-- Sub KPI -->
          <v-row class="mb-4">
            <v-col v-for="k in currKpiCards" :key="k.key" cols="6" sm="3">
              <div class="kpi-card" :style="`--c:${k.color}`">
                <div class="kpi-top">
                  <span class="kpi-label">{{ k.label }}</span>
                  <v-avatar :color="k.color" size="38"
                    ><v-icon dark size="18">{{ k.icon }}</v-icon></v-avatar
                  >
                </div>
                <div class="kpi-value">{{ formatNumber(k.value) }}</div>
                <div class="kpi-sub">{{ k.sub }}</div>
                <v-progress-linear
                  :value="k.pct"
                  :color="k.color"
                  height="3"
                  rounded
                  class="mt-2"
                  background-color="rgba(0,0,0,.06)"
                />
              </div>
            </v-col>
          </v-row>

          <!-- Filter bar -->
          <v-row class="mb-4">
            <v-col cols="12">
              <div class="panel px-4 py-3">
                <div class="d-flex align-center flex-wrap" style="gap:16px">
                  <div class="d-flex align-center" style="gap:8px">
                    <span class="filter-group-label">ระดับชั้น</span>
                    <div class="level-chips">
                      <div
                        v-for="lv in levelOptions"
                        :key="lv.value"
                        class="level-chip"
                        :class="{
                          active: currFilterLevel === lv.value,
                          [lv.cls]: true
                        }"
                        @click="
                          currFilterLevel =
                            currFilterLevel === lv.value ? null : lv.value
                        "
                      >
                        {{ lv.text }}
                      </div>
                    </div>
                  </div>
                  <div class="d-flex align-center" style="gap:8px">
                    <span class="filter-group-label">ประเภทวิชา</span>
                    <v-autocomplete
                      v-model="currFilterSubjectType"
                      :items="currSubjectTypeOpts"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      hide-details
                      clearable
                      placeholder="ทั้งหมด"
                      prepend-inner-icon="mdi-book-education"
                      style="width:220px"
                    />
                  </div>
                  <div class="d-flex align-center" style="gap:8px">
                    <span class="filter-group-label">แสดง Top</span>
                    <v-select
                      v-model="currTopN"
                      :items="[10, 20, 30, 50, 0]"
                      outlined
                      dense
                      hide-details
                      style="width:100px"
                    >
                      <template #item="{ item }">{{
                        item === 0 ? "ทั้งหมด" : item
                      }}</template>
                      <template #selection="{ item }">{{
                        item === 0 ? "ทั้งหมด" : item
                      }}</template>
                    </v-select>
                  </div>
                  <v-spacer />
                  <v-chip small outlined color="deep-purple">
                    <v-icon left small>mdi-filter-check</v-icon>INNER JOIN
                    curriculum curriculumYear ≥ 2567
                  </v-chip>
                  <v-btn
                    small
                    color="success"
                    :disabled="!currAllRanked.length"
                    @click="exportCurriculumExcel"
                  >
                    <v-icon left small>mdi-microsoft-excel</v-icon>ส่งออก Excel
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Chart + Risk -->
          <v-row class="mb-5">
            <v-col cols="12" md="7">
              <div class="panel">
                <div class="panel-head">
                  <v-icon small color="deep-orange" class="mr-2"
                    >mdi-chart-bar</v-icon
                  >
                  <span>สาขาวิชา เรียงตามจำนวนผู้เรียน (มาก → น้อย)</span>
                  <v-spacer />
                  <v-chip
                    x-small
                    :color="
                      currFilterLevel === '20'
                        ? 'blue'
                        : currFilterLevel === '30'
                        ? 'purple'
                        : 'teal'
                    "
                    dark
                  >
                    {{
                      currFilterLevel === "20"
                        ? "ปวช."
                        : currFilterLevel === "30"
                        ? "ปวส."
                        : "ปวช.+ปวส."
                    }}
                  </v-chip>
                </div>
                <apexchart
                  v-if="currChartSeries[0].data.length"
                  type="bar"
                  :height="currChartHeight"
                  :options="currChartOpts"
                  :series="currChartSeries"
                />
                <div
                  v-else
                  class="d-flex flex-column align-center justify-center"
                  style="height:280px;color:#94a3b8;font-size:.85rem"
                >
                  <v-icon color="grey lighten-1" size="48" class="mb-2"
                    >mdi-chart-bar</v-icon
                  >
                  <div v-if="loadingCurrJoin">กำลังโหลดข้อมูลหลักสูตร...</div>
                  <div v-else>ไม่พบข้อมูล</div>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="5">
              <v-row>
                <v-col cols="12">
                  <div class="panel">
                    <div class="panel-head">
                      <v-icon small color="indigo" class="mr-2"
                        >mdi-chart-donut</v-icon
                      >
                      <span>สัดส่วน ปวช. / ปวส. (หลักสูตร 2567+)</span>
                    </div>
                    <apexchart
                      type="donut"
                      height="200"
                      :options="currDonutOpts"
                      :series="currDonutSeries"
                    />
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="panel">
                    <div class="panel-head">
                      <v-icon small color="red darken-2" class="mr-2"
                        >mdi-alert-circle-outline</v-icon
                      >
                      <span>สาขาผู้เรียนน้อยที่สุด</span>
                      <v-spacer />
                      <v-chip
                        x-small
                        color="red lighten-4"
                        text-color="red darken-3"
                        >เสี่ยง</v-chip
                      >
                    </div>
                    <div class="curr-risk-list">
                      <div
                        v-if="!currRiskRows.length"
                        class="d-flex align-center justify-center"
                        style="height:140px;color:#94a3b8;font-size:.82rem"
                      >
                        ไม่พบข้อมูล
                      </div>
                      <div
                        v-for="(r, i) in currRiskRows"
                        :key="r.majorCode + '_' + r.level"
                        class="curr-risk-row"
                        :class="riskClass(r.total)"
                      >
                        <span class="curr-risk-rank">#{{ i + 1 }}</span>
                        <div class="curr-risk-info">
                          <div class="curr-risk-name">{{ r.majorName }}</div>
                          <div
                            class="d-flex align-center"
                            style="gap:4px;margin-top:2px"
                          >
                            <v-chip
                              x-small
                              dark
                              :color="r.level === '20' ? 'blue' : 'purple'"
                            >
                              {{ r.level === "20" ? "ปวช." : "ปวส." }}
                            </v-chip>
                            <span style="font-size:.68rem;color:#94a3b8">{{
                              r.subjectTypeName
                            }}</span>
                          </div>
                        </div>
                        <div class="text-right mr-1">
                          <strong style="font-size:.9rem;color:#ef5350">{{
                            formatNumber(r.total)
                          }}</strong>
                          <span
                            style="font-size:.68rem;color:#94a3b8;margin-left:2px"
                            >คน</span
                          >
                        </div>
                        <v-chip x-small :color="riskColor(r.total)" dark>{{
                          riskLabel(r.total)
                        }}</v-chip>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Ranking Table -->
          <v-row>
            <v-col cols="12">
              <div class="panel">
                <div class="panel-head flex-wrap" style="gap:8px">
                  <v-icon small color="primary" class="mr-2"
                    >mdi-table-search</v-icon
                  >
                  <span
                    >ตารางอันดับสาขาวิชา — หลักสูตร 2567+ (เรียงมาก →
                    น้อย)</span
                  >
                  <v-spacer />
                  <v-text-field
                    v-model="currTableSearch"
                    prepend-inner-icon="mdi-magnify"
                    label="ค้นหาสาขาวิชา..."
                    outlined
                    dense
                    clearable
                    hide-details
                    style="max-width:260px"
                  />
                  <v-chip small outlined color="primary"
                    >{{ currAllRanked.length }} สาขา</v-chip
                  >
                </div>
                <v-data-table
                  :headers="currHeaders"
                  :items="currAllRanked"
                  :search="currTableSearch"
                  :loading="loadingCurrJoin"
                  :items-per-page="25"
                  :footer-props="{
                    'items-per-page-options': [15, 25, 50, -1],
                    'items-per-page-text': 'แสดง:'
                  }"
                  class="rwc-table"
                  loading-text="กำลังโหลด..."
                  no-data-text="ไม่พบข้อมูล"
                >
                  <template #item.rank="{ item }">
                    <div class="rank-badge" :class="rankBadgeCls(item.rank)">
                      {{ item.rank }}
                    </div>
                  </template>
                  <template #item.level="{ item }">
                    <v-chip
                      x-small
                      dark
                      :color="item.level === '20' ? 'blue' : 'purple'"
                    >
                      {{ item.level === "20" ? "ปวช." : "ปวส." }}
                    </v-chip>
                  </template>
                  <template #item.majorName="{ item }">
                    <div class="cell-college">
                      <strong>{{ item.majorName }}</strong>
                      <span class="cell-province">{{ item.majorCode }}</span>
                    </div>
                  </template>
                  <template #item.subjectTypeName="{ item }">
                    <span class="cell-subject">{{ item.subjectTypeName }}</span>
                  </template>
                  <template #item.y1="{ item }"
                    ><span class="mono">{{
                      formatNumber(item.y1)
                    }}</span></template
                  >
                  <template #item.y2="{ item }"
                    ><span class="mono">{{
                      formatNumber(item.y2)
                    }}</span></template
                  >
                  <template #item.y3="{ item }"
                    ><span class="mono">{{
                      item.level === "20" ? formatNumber(item.y3) : "-"
                    }}</span></template
                  >
                  <template #item.total="{ item }">
                    <span class="mono font-weight-bold primary--text">{{
                      formatNumber(item.total)
                    }}</span>
                  </template>
                  <template #item.pct="{ item }">
                    <div class="pct-cell">
                      <div class="pct-bar-bg">
                        <div
                          class="pct-bar-fill"
                          :style="
                            `width:${item.pct}%;background:${
                              item.level === '20' ? '#1976d2' : '#7b1fa2'
                            }`
                          "
                        ></div>
                      </div>
                      <span class="pct-label">{{ item.pct }}%</span>
                    </div>
                  </template>
                  <template #item.colleges="{ item }"
                    ><span class="mono">{{ item.colleges }}</span></template
                  >
                  <template #item.risk="{ item }">
                    <v-chip x-small :color="riskColor(item.total)" dark>{{
                      riskLabel(item.total)
                    }}</v-chip>
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>
    </v-container>

    <!-- CSV Dialog -->
    <v-dialog v-model="csvDialog" max-width="560" persistent>
      <v-card class="dlg-card">
        <div class="dlg-head dlg-head--primary">
          <v-icon dark class="mr-2">mdi-file-delimited</v-icon
          >นำเข้าข้อมูลจากไฟล์ CSV
        </div>
        <v-card-text class="pt-5">
          <v-alert type="info" outlined dense class="mb-4 body-2">
            กรุณาเลือกไฟล์ CSV ที่มีรูปแบบตามที่กำหนด (ขนาดไม่เกิน 5 MB)
          </v-alert>
          <v-file-input
            v-model="selectedFile"
            label="เลือกไฟล์ CSV"
            accept=".csv"
            prepend-icon="mdi-file-upload"
            outlined
            dense
            show-size
            counter
            :rules="fileRules"
          >
            <template #selection="{ text }"
              ><v-chip small color="primary" dark>{{ text }}</v-chip></template
            >
          </v-file-input>
          <v-btn
            color="info"
            text
            small
            :to="'/admin/rate_work_college_import/'"
            target="_blank"
          >
            <v-icon left small>mdi-download</v-icon>ดาวน์โหลดไฟล์ตัวอย่าง
          </v-btn>
        </v-card-text>
        <v-card-actions class="pa-4 grey lighten-4">
          <v-spacer />
          <v-btn text @click="csvDialog = false"
            ><v-icon left small>mdi-close</v-icon>ยกเลิก</v-btn
          >
          <v-btn
            color="success"
            :loading="loading"
            :disabled="!selectedFile"
            @click="submitCsv"
          >
            <v-icon left small>mdi-upload</v-icon>นำเข้าข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- API Dialog -->
    <v-dialog v-model="apiDialog" max-width="460" persistent>
      <v-card class="dlg-card">
        <div class="dlg-head dlg-head--purple">
          <v-icon dark class="mr-2">mdi-cloud-download</v-icon>นำเข้าข้อมูลจาก
          API ศธ.02
        </div>
        <v-card-text class="pt-5">
          <v-alert type="info" outlined dense class="mb-4 body-2"
            >ระบบจะดึงข้อมูลนักเรียนจากระบบ ศธ.02 โดยอัตโนมัติ</v-alert
          >
          <v-text-field
            v-model="apiYear"
            label="ปีการศึกษา"
            type="number"
            prepend-inner-icon="mdi-calendar"
            outlined
            dense
            class="mb-3"
            :rules="[v => !!v || 'กรุณาระบุปีการศึกษา']"
          />
          <v-select
            v-model="apiSemester"
            :items="semesterOptions"
            item-text="text"
            item-value="value"
            label="ข้อมูล ณ วันที่"
            prepend-inner-icon="mdi-calendar-range"
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions class="pa-4 grey lighten-4">
          <v-spacer />
          <v-btn text @click="apiDialog = false"
            ><v-icon left small>mdi-close</v-icon>ยกเลิก</v-btn
          >
          <v-btn
            color="deep-purple"
            dark
            :loading="loading"
            :disabled="!apiYear || !apiSemester"
            @click="submitApi"
          >
            <v-icon left small>mdi-cloud-sync</v-icon>ดึงข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      :timeout="4000"
      top
      right
    >
      <v-icon class="mr-2">{{ snack.icon }}</v-icon
      >{{ snack.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snack.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as XLSX from "xlsx";

const PROVINCE_EN_TO_ID = {
  "Chiang Mai": 50,
  Chiangmai: 50,
  "Chiang Rai": 57,
  Chiangrai: 57,
  Nan: 55,
  Phayao: 56,
  Phrae: 54,
  "Mae Hong Son": 58,
  Maehongson: 58,
  Lampang: 52,
  Lamphun: 51,
  Uttaradit: 53,
  Tak: 63,
  Sukhothai: 64,
  Phitsanulok: 65,
  Phichit: 66,
  Phetchabun: 67,
  "Kamphaeng Phet": 62,
  Kamphaengphet: 62,
  "Nakhon Sawan": 60,
  Nakhonsawan: 60,
  "Uthai Thani": 61,
  Uthaithani: 61,
  "Phra Nakhon Si Ayutthaya": 14,
  Ayutthaya: 14,
  "Ang Thong": 15,
  Angthong: 15,
  Lopburi: 16,
  "Lop Buri": 16,
  "Sing Buri": 17,
  Singburi: 17,
  "Chai Nat": 18,
  Chainat: 18,
  Saraburi: 19,
  Ratchaburi: 70,
  Kanchanaburi: 71,
  "Suphan Buri": 72,
  Suphanburi: 72,
  "Samut Songkhram": 75,
  Samutsongkhram: 75,
  Phetchaburi: 76,
  "Prachuap Khiri Khan": 77,
  Prachuapkhirikhan: 77,
  Bangkok: 10,
  "Krung Thep": 10,
  "Samut Prakan": 11,
  Samutprakan: 11,
  Nonthaburi: 12,
  "Pathum Thani": 13,
  Pathumthani: 13,
  "Nakhon Pathom": 73,
  Nakhonpathom: 73,
  "Samut Sakhon": 74,
  Samutsakhon: 74,
  "Chon Buri": 20,
  Chonburi: 20,
  Rayong: 21,
  Chanthaburi: 22,
  Trat: 23,
  Chachoengsao: 24,
  "Prachin Buri": 25,
  Prachinburi: 25,
  "Nakhon Nayok": 26,
  Nakhonnayok: 26,
  "Sa Kaeo": 27,
  Sakaeo: 27,
  "Sa Kaew": 27,
  "Nakhon Ratchasima": 30,
  Nakhonratchasima: 30,
  Korat: 30,
  "Buri Ram": 31,
  Buriram: 31,
  Surin: 32,
  "Si Sa Ket": 33,
  Sisaket: 33,
  "Ubon Ratchathani": 34,
  Ubonratchathani: 34,
  Yasothon: 35,
  Chaiyaphum: 36,
  "Amnat Charoen": 37,
  Amnatcharoen: 37,
  "Bueng Kan": 38,
  Buengkan: 38,
  "Nong Bua Lam Phu": 39,
  Nongbualamphu: 39,
  "Khon Kaen": 40,
  Khonkaen: 40,
  "Udon Thani": 41,
  Udonthan: 41,
  Loei: 42,
  "Nong Khai": 43,
  Nongkhai: 43,
  "Maha Sarakham": 44,
  Mahasarakham: 44,
  "Roi Et": 45,
  Roiet: 45,
  Kalasin: 46,
  "Sakon Nakhon": 47,
  Sakonnakhon: 47,
  "Nakhon Phanom": 48,
  Nakhonphanom: 48,
  Mukdahan: 49,
  "Nakhon Si Thammarat": 80,
  Nakhonsithammarat: 80,
  Krabi: 81,
  "Phang Nga": 82,
  Phangnga: 82,
  Phuket: 83,
  "Surat Thani": 84,
  Suratthani: 84,
  Ranong: 85,
  Chumphon: 86,
  Songkhla: 90,
  Satun: 91,
  Trang: 92,
  Phatthalung: 93,
  Pattani: 94,
  Yala: 95,
  Narathiwat: 96
};
const PROVINCE_META = [
  { id: "50", name: "เชียงใหม่", region: "ภาคเหนือ", base: 51000 },
  { id: "51", name: "ลำพูน", region: "ภาคเหนือ", base: 8800 },
  { id: "52", name: "ลำปาง", region: "ภาคเหนือ", base: 14800 },
  { id: "53", name: "อุตรดิตถ์", region: "ภาคเหนือ", base: 9800 },
  { id: "54", name: "แพร่", region: "ภาคเหนือ", base: 8800 },
  { id: "55", name: "น่าน", region: "ภาคเหนือ", base: 7800 },
  { id: "56", name: "พะเยา", region: "ภาคเหนือ", base: 7800 },
  { id: "57", name: "เชียงราย", region: "ภาคเหนือ", base: 21500 },
  { id: "58", name: "แม่ฮ่องสอน", region: "ภาคเหนือ", base: 3800 },
  { id: "60", name: "นครสวรรค์", region: "ภาคเหนือ", base: 19000 },
  { id: "61", name: "อุทัยธานี", region: "ภาคเหนือ", base: 5800 },
  { id: "62", name: "กำแพงเพชร", region: "ภาคเหนือ", base: 10500 },
  { id: "63", name: "ตาก", region: "ภาคเหนือ", base: 7800 },
  { id: "64", name: "สุโขทัย", region: "ภาคเหนือ", base: 9800 },
  { id: "65", name: "พิษณุโลก", region: "ภาคเหนือ", base: 21000 },
  { id: "66", name: "พิจิตร", region: "ภาคเหนือ", base: 7700 },
  { id: "67", name: "เพชรบูรณ์", region: "ภาคเหนือ", base: 13800 },
  { id: "14", name: "พระนครศรีอยุธยา", region: "ภาคกลาง", base: 22000 },
  { id: "15", name: "อ่างทอง", region: "ภาคกลาง", base: 5500 },
  { id: "16", name: "ลพบุรี", region: "ภาคกลาง", base: 17000 },
  { id: "17", name: "สิงห์บุรี", region: "ภาคกลาง", base: 5500 },
  { id: "18", name: "ชัยนาท", region: "ภาคกลาง", base: 6500 },
  { id: "19", name: "สระบุรี", region: "ภาคกลาง", base: 13500 },
  { id: "70", name: "ราชบุรี", region: "ภาคกลาง", base: 17500 },
  { id: "71", name: "กาญจนบุรี", region: "ภาคกลาง", base: 13500 },
  { id: "72", name: "สุพรรณบุรี", region: "ภาคกลาง", base: 13500 },
  { id: "75", name: "สมุทรสงคราม", region: "ภาคกลาง", base: 4800 },
  { id: "76", name: "เพชรบุรี", region: "ภาคกลาง", base: 9500 },
  { id: "77", name: "ประจวบคีรีขันธ์", region: "ภาคกลาง", base: 7800 },
  {
    id: "10",
    name: "กรุงเทพมหานคร",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 120000
  },
  {
    id: "11",
    name: "สมุทรปราการ",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 42000
  },
  {
    id: "12",
    name: "นนทบุรี",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 30000
  },
  {
    id: "13",
    name: "ปทุมธานี",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 28000
  },
  {
    id: "73",
    name: "นครปฐม",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 22000
  },
  {
    id: "74",
    name: "สมุทรสาคร",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 18000
  },
  {
    id: "20",
    name: "ชลบุรี",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 52000
  },
  {
    id: "21",
    name: "ระยอง",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 28000
  },
  {
    id: "22",
    name: "จันทบุรี",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 11500
  },
  {
    id: "23",
    name: "ตราด",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 4800
  },
  {
    id: "24",
    name: "ฉะเชิงเทรา",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 15500
  },
  {
    id: "25",
    name: "ปราจีนบุรี",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 9800
  },
  {
    id: "26",
    name: "นครนายก",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 6800
  },
  {
    id: "27",
    name: "สระแก้ว",
    region: "ภาคตะวันออก กรุงเทพและปริมณฑล",
    base: 8800
  },
  {
    id: "30",
    name: "นครราชสีมา",
    region: "ภาคตะวันออกเฉียงเหนือ",
    base: 55000
  },
  { id: "31", name: "บุรีรัมย์", region: "ภาคตะวันออกเฉียงเหนือ", base: 21000 },
  { id: "32", name: "สุรินทร์", region: "ภาคตะวันออกเฉียงเหนือ", base: 19500 },
  { id: "33", name: "ศรีสะเกษ", region: "ภาคตะวันออกเฉียงเหนือ", base: 21500 },
  {
    id: "34",
    name: "อุบลราชธานี",
    region: "ภาคตะวันออกเฉียงเหนือ",
    base: 41000
  },
  { id: "35", name: "ยโสธร", region: "ภาคตะวันออกเฉียงเหนือ", base: 9800 },
  { id: "36", name: "ชัยภูมิ", region: "ภาคตะวันออกเฉียงเหนือ", base: 14500 },
  { id: "37", name: "อำนาจเจริญ", region: "ภาคตะวันออกเฉียงเหนือ", base: 7800 },
  { id: "38", name: "บึงกาฬ", region: "ภาคตะวันออกเฉียงเหนือ", base: 6800 },
  {
    id: "39",
    name: "หนองบัวลำภู",
    region: "ภาคตะวันออกเฉียงเหนือ",
    base: 7800
  },
  { id: "40", name: "ขอนแก่น", region: "ภาคตะวันออกเฉียงเหนือ", base: 47000 },
  { id: "41", name: "อุดรธานี", region: "ภาคตะวันออกเฉียงเหนือ", base: 39000 },
  { id: "42", name: "เลย", region: "ภาคตะวันออกเฉียงเหนือ", base: 11500 },
  { id: "43", name: "หนองคาย", region: "ภาคตะวันออกเฉียงเหนือ", base: 9800 },
  { id: "44", name: "มหาสารคาม", region: "ภาคตะวันออกเฉียงเหนือ", base: 17500 },
  { id: "45", name: "ร้อยเอ็ด", region: "ภาคตะวันออกเฉียงเหนือ", base: 19500 },
  { id: "46", name: "กาฬสินธุ์", region: "ภาคตะวันออกเฉียงเหนือ", base: 13500 },
  { id: "47", name: "สกลนคร", region: "ภาคตะวันออกเฉียงเหนือ", base: 17500 },
  { id: "48", name: "นครพนม", region: "ภาคตะวันออกเฉียงเหนือ", base: 9800 },
  { id: "49", name: "มุกดาหาร", region: "ภาคตะวันออกเฉียงเหนือ", base: 6800 },
  { id: "80", name: "นครศรีธรรมราช", region: "ภาคใต้", base: 27500 },
  { id: "81", name: "กระบี่", region: "ภาคใต้", base: 7800 },
  { id: "82", name: "พังงา", region: "ภาคใต้", base: 4800 },
  { id: "83", name: "ภูเก็ต", region: "ภาคใต้", base: 13800 },
  { id: "84", name: "สุราษฎร์ธานี", region: "ภาคใต้", base: 17800 },
  { id: "85", name: "ระนอง", region: "ภาคใต้", base: 3800 },
  { id: "86", name: "ชุมพร", region: "ภาคใต้", base: 7800 },
  { id: "90", name: "สงขลา", region: "ภาคใต้", base: 27500 },
  { id: "91", name: "สตูล", region: "ภาคใต้", base: 4800 },
  { id: "92", name: "ตรัง", region: "ภาคใต้", base: 9800 },
  { id: "93", name: "พัทลุง", region: "ภาคใต้", base: 7800 },
  { id: "94", name: "ปัตตานี", region: "ภาคใต้", base: 7800 },
  { id: "95", name: "ยะลา", region: "ภาคใต้", base: 5800 },
  { id: "96", name: "นราธิวาส", region: "ภาคใต้", base: 5800 }
];

export default {
  name: "RateWorkCollegeAdmin",

  data() {
    const y = new Date().getFullYear() + 543;
    return {
      loading: false,
      loadingCurriculum: false,
      loadingCurrJoin: false,
      ApiKey: "HRvec2021",
      currentYear: y,
      activeTab: 0,

      // ── raw data ──
      rate_work_colleges: [],
      rate_work_collegesReport: [],
      colleges: [],
      curriculumList: [],
      currJoinRows: [], // ← JOIN result curriculum year >= 2567

      // ── search / filters (Tab 0) ──
      search: "",
      searchYear: y,
      collegeSearch: "",
      selectedFile: null,
      showAdvanced: false,
      filterLevels: [],
      filterRegion: null,
      filterProvince: [],
      filterSubjectTypes: [],
      filterMajors: [],
      filterStudentRange: [0, 0],
      studentRangeMax: 500,
      studentRangeStep: 50,

      // ── filters (Tab 1 curriculum) ──
      currFilterLevel: null,
      currFilterSubjectType: null,
      currTopN: 20,
      currTableSearch: "",

      levelOptions: [
        { text: "ปวช.", value: "20", cls: "chip-vc" },
        { text: "ปวส.", value: "30", cls: "chip-hvc" }
      ],
      regionOptions: [
        { text: "ภาคเหนือ", value: "ภาคเหนือ" },
        { text: "ภาคกลาง", value: "ภาคกลาง" },
        {
          text: "ภาคตะวันออก กรุงเทพและปริมณฑล",
          value: "ภาคตะวันออก กรุงเทพและปริมณฑล"
        },
        { text: "ภาคตะวันออกเฉียงเหนือ", value: "ภาคตะวันออกเฉียงเหนือ" },
        { text: "ภาคใต้", value: "ภาคใต้" }
      ],

      // ── dialogs ──
      csvDialog: false,
      apiDialog: false,
      apiYear: y,
      apiSemester: "",
      semesterOptions: [
        { text: "10 มิถุนายน", value: "1" },
        { text: "10 พฤศจิกายน", value: "2" }
      ],
      fileRules: [
        v => !!v || "กรุณาเลือกไฟล์",
        v => (v && v.size < 5000000) || "ขนาดไฟล์ต้องน้อยกว่า 5 MB"
      ],

      // ── snackbar ──
      snack: { show: false, color: "", icon: "", text: "" },

      // ── leaflet map ──
      leafletMap: null,
      leafletGeoLayer: null,
      geoData: null,
      geoReady: false,
      mapMode: "students",
      mapTip: {
        show: false,
        x: 0,
        y: 0,
        province: "",
        region: "",
        students: 0,
        colleges: 0,
        pct: 0
      },
      selectedProvince: null,
      provinceDataMap: {},

      // ── table headers (Tab 0) ──
      headers: [
        {
          text: "ปีการศึกษา",
          value: "rate_work_college_year",
          align: "center",
          width: "90"
        },
        {
          text: "วิทยาลัย",
          value: "college_name",
          align: "left",
          width: "200"
        },
        {
          text: "ประเภทวิชา",
          value: "subject_typeName",
          align: "center",
          width: "110"
        },
        {
          text: "ระดับ",
          value: "rate_work_college_id_class",
          align: "center",
          width: "70"
        },
        {
          text: "สาขาวิชา",
          value: "name_type_course",
          align: "left",
          width: "190"
        },
        {
          text: "ปี 1",
          value: "rate_work_field_study_lavel_1",
          align: "right",
          width: "70"
        },
        {
          text: "ปี 2",
          value: "rate_work_field_study_lavel_2",
          align: "right",
          width: "70"
        },
        {
          text: "ปี 3",
          value: "rate_work_field_study_lavel_3",
          align: "right",
          width: "70"
        },
        { text: "รวม", value: "total_row", align: "right", width: "80" }
      ],

      // ── table headers (Tab 1) ──
      currHeaders: [
        {
          text: "อันดับ",
          value: "rank",
          align: "center",
          width: "70",
          sortable: false
        },
        { text: "ระดับ", value: "level", align: "center", width: "72" },
        { text: "สาขาวิชา", value: "majorName", align: "left", width: "220" },
        {
          text: "ประเภทวิชา",
          value: "subjectTypeName",
          align: "center",
          width: "130"
        },
        { text: "ปี 1", value: "y1", align: "right", width: "72" },
        { text: "ปี 2", value: "y2", align: "right", width: "72" },
        { text: "ปี 3", value: "y3", align: "right", width: "72" },
        { text: "รวม", value: "total", align: "right", width: "90" },
        {
          text: "สัดส่วน",
          value: "pct",
          align: "left",
          width: "120",
          sortable: false
        },
        { text: "วิทยาลัย", value: "colleges", align: "right", width: "80" },
        {
          text: "สถานะ",
          value: "risk",
          align: "center",
          width: "90",
          sortable: false
        }
      ]
    };
  },

  // ═══════════════════════════ COMPUTED ═══════════════════════════
  computed: {
    years() {
      const arr = [];
      for (let i = this.currentYear - 3; i <= this.currentYear + 2; i++)
        arr.push(i);
      return arr;
    },

    // ─── Tab 0 computed ───
    regionSummary() {
      const map = {};
      this.filteredData.forEach(i => {
        const r = i.region_name || "ไม่ระบุ";
        if (!map[r])
          map[r] = {
            region: r,
            students: 0,
            colleges: new Set(),
            courses: new Set()
          };
        const l1 = parseInt(i.rate_work_field_study_lavel_1) || 0;
        const l2 = parseInt(i.rate_work_field_study_lavel_2) || 0;
        const l3 = parseInt(i.rate_work_field_study_lavel_3) || 0;
        map[r].students += l1 + l2 + l3;
        map[r].colleges.add(i.rate_work_college_code);
        if (i.name_type_course) map[r].courses.add(i.name_type_course);
      });
      const rows = Object.values(map)
        .map(r => ({
          region: r.region,
          students: r.students,
          colleges: r.colleges.size,
          courses: r.courses.size
        }))
        .sort((a, b) => b.students - a.students);
      const maxS = rows.length ? Math.max(...rows.map(r => r.students)) : 1;
      return rows.map(r => ({
        ...r,
        pct: maxS > 0 ? Math.round((r.students / maxS) * 100) : 0
      }));
    },

    advFilterCount() {
      return (
        this.filterLevels.length +
        this.filterSubjectTypes.length +
        this.filterMajors.length +
        (this.filterRegion ? 1 : 0) +
        this.filterProvince.length +
        (this.filterStudentRange[0] > 0 ||
        this.filterStudentRange[1] < this.studentRangeMax
          ? 1
          : 0)
      );
    },
    totalActiveFilters() {
      return this.advFilterCount + (this.collegeSearch ? 1 : 0);
    },

    provinceOptions() {
      return PROVINCE_META.filter(
        p => !this.filterRegion || p.region === this.filterRegion
      )
        .map(p => ({ text: p.name, value: p.name }))
        .sort((a, b) => a.text.localeCompare(b.text, "th"));
    },

    subjectTypeOptions() {
      const seen = new Set();
      const r = [];
      for (const c of this.curriculumList) {
        if (this.filterLevels.length > 0) {
          const cls = String(c.degreeLevelID) === "10" ? "20" : "30";
          if (!this.filterLevels.includes(cls)) continue;
        }
        const k = String(c.subjectTypeID);
        if (seen.has(k)) continue;
        seen.add(k);
        r.push({
          subjectTypeID: k,
          subjectTypeNameTh: c.subjectTypeNameTh || "",
          subjectTypeNameEn: c.subjectTypeNameEn || ""
        });
      }
      return r.sort((a, b) =>
        (a.subjectTypeNameTh || "").localeCompare(
          b.subjectTypeNameTh || "",
          "th"
        )
      );
    },
    majorOptions() {
      if (!this.filterSubjectTypes.length) return [];
      const seen = new Set();
      const r = [];
      for (const c of this.curriculumList) {
        if (!this.filterSubjectTypes.includes(String(c.subjectTypeID)))
          continue;
        if (this.filterLevels.length > 0) {
          const cls = String(c.degreeLevelID) === "10" ? "20" : "30";
          if (!this.filterLevels.includes(cls)) continue;
        }
        const k = String(c.majorID);
        if (seen.has(k)) continue;
        seen.add(k);
        r.push({
          majorID: k,
          majorNameTh: c.majorNameTh || "",
          majorNameEn: c.majorNameEn || ""
        });
      }
      return r.sort((a, b) =>
        (a.majorNameTh || "").localeCompare(b.majorNameTh || "", "th")
      );
    },

    filteredData() {
      let d = this.rate_work_colleges;
      if (this.filterLevels.length)
        d = d.filter(i =>
          this.filterLevels.includes(String(i.rate_work_college_id_class))
        );
      if (this.filterRegion)
        d = d.filter(i => i.region_name === this.filterRegion);
      if (this.filterProvince.length)
        d = d.filter(i => this.filterProvince.includes(i.province_name));
      if (this.filterSubjectTypes.length) {
        const names = new Set(
          this.filterSubjectTypes
            .map(id => this.getSubjectTypeName(id))
            .filter(Boolean)
        );
        d = d.filter(i => names.has(i.subject_typeName));
      }
      if (this.filterMajors.length) {
        const names = new Set(
          this.filterMajors.map(id => this.getMajorName(id)).filter(Boolean)
        );
        d = d.filter(i => names.has(i.name_type_course));
      }
      d = d.map(i => {
        const l1 = parseInt(i.rate_work_field_study_lavel_1) || 0;
        const l2 = parseInt(i.rate_work_field_study_lavel_2) || 0;
        const l3 = parseInt(i.rate_work_field_study_lavel_3) || 0;
        const isVC = String(i.rate_work_college_id_class) === "20";
        return { ...i, total_row: isVC ? l1 + l2 + l3 : l1 + l2 };
      });
      const [lo, hi] = this.filterStudentRange;
      if (lo > 0 || hi < this.studentRangeMax)
        d = d.filter(i => i.total_row >= lo && i.total_row <= hi);
      return d;
    },

    statistics() {
      let ch1 = 0,
        ch2 = 0,
        ch3 = 0,
        s1 = 0,
        s2 = 0;
      this.filteredData.forEach(i => {
        const l1 = parseInt(i.rate_work_field_study_lavel_1) || 0;
        const l2 = parseInt(i.rate_work_field_study_lavel_2) || 0;
        const l3 = parseInt(i.rate_work_field_study_lavel_3) || 0;
        if (String(i.rate_work_college_id_class) === "20") {
          ch1 += l1;
          ch2 += l2;
          ch3 += l3;
        } else {
          s1 += l1;
          s2 += l2;
        }
      });
      const totalVC = ch1 + ch2 + ch3,
        totalHVC = s1 + s2,
        total = totalVC + totalHVC;
      const courses = new Set(this.filteredData.map(i => i.name_type_course))
        .size;
      const colleges = new Set(
        this.filteredData.map(i => i.rate_work_college_code)
      ).size;
      return {
        ch1,
        ch2,
        ch3,
        s1,
        s2,
        totalVC,
        totalHVC,
        total,
        courses,
        colleges,
        vcPct: total > 0 ? +((totalVC / total) * 100).toFixed(1) : 0,
        hvcPct: total > 0 ? +((totalHVC / total) * 100).toFixed(1) : 0
      };
    },
    totalStudents() {
      return this.statistics.total;
    },

    kpiCards() {
      const s = this.statistics;
      return [
        {
          key: "vc",
          label: "นักเรียน ปวช.",
          value: s.totalVC,
          sub: `${s.vcPct}% ของทั้งหมด`,
          icon: "mdi-account-school",
          color: "#1976d2",
          pct: s.vcPct
        },
        {
          key: "hvc",
          label: "นักศึกษา ปวส.",
          value: s.totalHVC,
          sub: `${s.hvcPct}% ของทั้งหมด`,
          icon: "mdi-school",
          color: "#7b1fa2",
          pct: s.hvcPct
        },
        {
          key: "ttl",
          label: "รวมทั้งหมด",
          value: s.total,
          sub: "นักเรียน นักศึกษา",
          icon: "mdi-account-group",
          color: "#00796b",
          pct: 100
        },
        {
          key: "crs",
          label: "จำนวนสาขาวิชา",
          value: s.courses,
          sub: "สาขาวิชา",
          icon: "mdi-book-open-variant",
          color: "#e65100",
          pct: 100
        }
      ];
    },
    summaryRows() {
      const s = this.statistics;
      return [
        { label: "ปวช.1", val: s.ch1, cls: "" },
        { label: "ปวช.2", val: s.ch2, cls: "" },
        { label: "ปวช.3", val: s.ch3, cls: "sum-vc-row" },
        { label: "ปวส.1", val: s.s1, cls: "sum-sep-row" },
        { label: "ปวส.2", val: s.s2, cls: "sum-hvc-row" },
        { label: "รวม", val: s.total, cls: "sum-total-row" }
      ];
    },
    studentRangeLabel() {
      const [lo, hi] = this.filterStudentRange;
      if (lo === 0 && hi === this.studentRangeMax) return "ทั้งหมด";
      return `${this.formatNumber(lo)} – ${this.formatNumber(hi)} คน`;
    },

    bottomCourseSeries() {
      const map = {};
      this.filteredData.forEach(i => {
        const n = i.name_type_course;
        if (!n) return;
        map[n] = (map[n] || 0) + (i.total_row || 0);
      });
      const entries = Object.entries(map)
        .filter(([, v]) => v > 0)
        .sort((a, b) => a[1] - b[1])
        .slice(0, 10);
      return [{ data: entries.map(([x, y]) => ({ x, y })) }];
    },
    bottomCourseOpts() {
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun,sans-serif",
          toolbar: { show: false }
        },
        plotOptions: {
          bar: { borderRadius: 4, horizontal: true, distributed: true }
        },
        colors: [
          "#ef5350",
          "#e53935",
          "#b71c1c",
          "#f44336",
          "#ef9a9a",
          "#ff7043",
          "#ff5722",
          "#bf360c",
          "#d84315",
          "#e64a19"
        ],
        xaxis: {
          labels: {
            formatter: v => Math.round(v / 1000) + "K",
            style: { fontSize: "10px", fontFamily: "Sarabun" }
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: "10px", fontFamily: "Sarabun" },
            maxWidth: 160
          }
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter: v => v.toLocaleString("th-TH"),
          style: { fontSize: "10px", fontFamily: "Sarabun" }
        },
        tooltip: { y: { formatter: v => v.toLocaleString("th-TH") + " คน" } }
      };
    },
    topCourseSeries() {
      const map = {};
      this.filteredData.forEach(i => {
        const n = i.name_type_course;
        if (!n) return;
        map[n] = (map[n] || 0) + (i.total_row || 0);
      });
      const entries = Object.entries(map)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);
      return [
        { name: "จำนวนนักเรียน", data: entries.map(([x, y]) => ({ x, y })) }
      ];
    },
    topCourseOpts() {
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun,sans-serif",
          toolbar: { show: false },
          animations: { speed: 500 }
        },
        plotOptions: {
          bar: {
            borderRadius: 5,
            horizontal: true,
            distributed: true,
            barHeight: "70%",
            dataLabels: { position: "bottom" }
          }
        },
        colors: [
          "#1e3a8a",
          "#1565c0",
          "#1976d2",
          "#1e88e5",
          "#42a5f5",
          "#64b5f6",
          "#6a1b9a",
          "#7b1fa2",
          "#00796b",
          "#26a69a"
        ],
        xaxis: {
          labels: {
            formatter: v => (v >= 1000 ? Math.round(v / 1000) + "K" : v),
            style: { fontSize: "10px", fontFamily: "Sarabun" }
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: "11px", fontFamily: "Sarabun" },
            maxWidth: 200
          }
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          textAnchor: "start",
          offsetX: 6,
          formatter: v => v.toLocaleString("th-TH"),
          style: {
            fontSize: "10px",
            fontFamily: "Sarabun",
            colors: ["#334155"]
          }
        },
        tooltip: { y: { formatter: v => v.toLocaleString("th-TH") + " คน" } },
        grid: {
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: false } }
        }
      };
    },
    donutSeries() {
      return [this.statistics.totalVC, this.statistics.totalHVC];
    },
    donutOpts() {
      return {
        chart: {
          type: "donut",
          fontFamily: "Sarabun,sans-serif",
          animations: { speed: 600 }
        },
        labels: ["ปวช.", "ปวส."],
        colors: ["#1976d2", "#7b1fa2"],
        legend: { position: "bottom", fontSize: "13px" },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "รวม",
                  fontFamily: "Sarabun",
                  formatter: w =>
                    w.globals.seriesTotals
                      .reduce((a, b) => a + b, 0)
                      .toLocaleString("th-TH")
                }
              }
            }
          }
        },
        dataLabels: { style: { fontFamily: "Sarabun" } },
        tooltip: { y: { formatter: v => v.toLocaleString("th-TH") + " คน" } }
      };
    },
    levelBarSeries() {
      const s = this.statistics;
      return [
        { name: "จำนวนนักเรียน", data: [s.ch1, s.ch2, s.ch3, s.s1, s.s2] }
      ];
    },
    levelBarOpts() {
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun,sans-serif",
          toolbar: { show: false }
        },
        plotOptions: {
          bar: { borderRadius: 5, columnWidth: "55%", distributed: true }
        },
        colors: ["#1565c0", "#1976d2", "#42a5f5", "#6a1b9a", "#9c27b0"],
        xaxis: {
          categories: ["ปวช.1", "ปวช.2", "ปวช.3", "ปวส.1", "ปวส.2"],
          labels: { style: { fontSize: "11px", fontFamily: "Sarabun" } }
        },
        yaxis: {
          labels: {
            formatter: v => Math.round(v / 1000) + "K",
            style: { fontSize: "10px", fontFamily: "Sarabun" }
          }
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter: v => v.toLocaleString("th-TH"),
          style: { fontSize: "10px", fontFamily: "Sarabun" }
        },
        tooltip: { y: { formatter: v => v.toLocaleString("th-TH") + " คน" } }
      };
    },

    // ─── Tab 1 computed: curriculum join ───
    currFiltered() {
      let d = this.currJoinRows;
      if (this.currFilterLevel)
        d = d.filter(
          r => String(r.rate_work_college_id_class) === this.currFilterLevel
        );
      if (this.currFilterSubjectType)
        d = d.filter(
          r => String(r.subjectTypeID) === String(this.currFilterSubjectType)
        );
      return d;
    },
    currAggregated() {
      const map = {};
      this.currFiltered.forEach(r => {
        const key = `${r.majorCode}__${r.rate_work_college_id_class}`;
        if (!map[key]) {
          map[key] = {
            majorCode: r.majorCode,
            majorName: r.majorNameTh || r.majorCode,
            subjectTypeName: r.subjectTypeNameTh || "",
            level: String(r.rate_work_college_id_class),
            y1: 0,
            y2: 0,
            y3: 0,
            colleges: new Set()
          };
        }
        map[key].y1 += parseInt(r.rate_work_field_study_lavel_1) || 0;
        map[key].y2 += parseInt(r.rate_work_field_study_lavel_2) || 0;
        map[key].y3 += parseInt(r.rate_work_field_study_lavel_3) || 0;
        if (r.rate_work_college_code)
          map[key].colleges.add(r.rate_work_college_code);
      });
      return Object.values(map)
        .map(m => ({
          ...m,
          colleges: m.colleges.size,
          total: m.level === "20" ? m.y1 + m.y2 + m.y3 : m.y1 + m.y2
        }))
        .sort((a, b) => b.total - a.total);
    },
    currAllRanked() {
      const maxTotal = this.currAggregated.length
        ? this.currAggregated[0].total
        : 1;
      return this.currAggregated.map((r, i) => ({
        ...r,
        rank: i + 1,
        pct: maxTotal > 0 ? Math.round((r.total / maxTotal) * 100) : 0
      }));
    },
    currRankedRows() {
      return this.currTopN > 0
        ? this.currAllRanked.slice(0, this.currTopN)
        : this.currAllRanked;
    },
    currRiskRows() {
      return [...this.currAggregated]
        .filter(r => r.total > 0)
        .sort((a, b) => a.total - b.total)
        .slice(0, 10);
    },
    currVcTotal() {
      return this.currAggregated
        .filter(r => r.level === "20")
        .reduce((s, r) => s + r.total, 0);
    },
    currHvcTotal() {
      return this.currAggregated
        .filter(r => r.level === "30")
        .reduce((s, r) => s + r.total, 0);
    },
    currVcMajors() {
      return this.currAggregated.filter(r => r.level === "20").length;
    },
    currHvcMajors() {
      return this.currAggregated.filter(r => r.level === "30").length;
    },
    currKpiCards() {
      const t = this.currVcTotal + this.currHvcTotal || 1;
      return [
        {
          key: "cv",
          label: "นักเรียน ปวช. (2567+)",
          value: this.currVcTotal,
          sub: `${Math.round((this.currVcTotal / t) * 100)}% ของรวม`,
          icon: "mdi-account-school",
          color: "#1976d2",
          pct: Math.round((this.currVcTotal / t) * 100)
        },
        {
          key: "ch",
          label: "นักศึกษา ปวส. (2567+)",
          value: this.currHvcTotal,
          sub: `${Math.round((this.currHvcTotal / t) * 100)}% ของรวม`,
          icon: "mdi-school",
          color: "#7b1fa2",
          pct: Math.round((this.currHvcTotal / t) * 100)
        },
        {
          key: "cvm",
          label: "สาขา ปวช. (2567+)",
          value: this.currVcMajors,
          sub: "สาขาวิชา ปวช.",
          icon: "mdi-book-open-variant",
          color: "#0288d1",
          pct: Math.round(
            (this.currVcMajors /
              (this.currVcMajors + this.currHvcMajors || 1)) *
              100
          )
        },
        {
          key: "chm",
          label: "สาขา ปวส. (2567+)",
          value: this.currHvcMajors,
          sub: "สาขาวิชา ปวส.",
          icon: "mdi-book-education",
          color: "#6a1b9a",
          pct: Math.round(
            (this.currHvcMajors /
              (this.currVcMajors + this.currHvcMajors || 1)) *
              100
          )
        }
      ];
    },
    currSubjectTypeOpts() {
      const seen = new Set();
      const r = [];
      this.currJoinRows.forEach(row => {
        const k = String(row.subjectTypeID);
        if (seen.has(k)) return;
        seen.add(k);
        r.push({ id: k, name: row.subjectTypeNameTh || k });
      });
      return r.sort((a, b) => (a.name || "").localeCompare(b.name || "", "th"));
    },
    currChartSeries() {
      const data = this.currRankedRows.map(r => ({
        x: r.majorName,
        y: r.total
      }));
      return [{ name: "จำนวนผู้เรียน", data }];
    },
    currChartHeight() {
      const n = Math.min(
        this.currRankedRows.length,
        this.currTopN > 0 ? this.currTopN : 50
      );
      return Math.max(300, n * 30);
    },
    currChartOpts() {
      const isVC = this.currFilterLevel === "20",
        isHVC = this.currFilterLevel === "30";
      const colors = isVC
        ? [
            "#1565c0",
            "#1976d2",
            "#1e88e5",
            "#42a5f5",
            "#64b5f6",
            "#90caf9",
            "#bbdefb",
            "#1a237e",
            "#283593",
            "#3949ab"
          ]
        : isHVC
        ? [
            "#4a148c",
            "#6a1b9a",
            "#7b1fa2",
            "#8e24aa",
            "#9c27b0",
            "#ab47bc",
            "#ba68c8",
            "#311b92",
            "#4527a0",
            "#512da8"
          ]
        : [
            "#1976d2",
            "#7b1fa2",
            "#00796b",
            "#e65100",
            "#c62828",
            "#0288d1",
            "#388e3c",
            "#f57c00",
            "#5d4037",
            "#455a64",
            "#1565c0",
            "#6a1b9a",
            "#00695c",
            "#bf360c",
            "#b71c1c",
            "#01579b",
            "#2e7d32",
            "#e65100",
            "#4e342e",
            "#37474f"
          ];
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun,sans-serif",
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
            distributed: true,
            barHeight: "72%"
          }
        },
        colors,
        xaxis: {
          labels: {
            formatter: v => (v >= 1000 ? Math.round(v / 1000) + "K" : v),
            style: { fontSize: "10px", fontFamily: "Sarabun" }
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: "10px", fontFamily: "Sarabun" },
            maxWidth: 200
          }
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          textAnchor: "start",
          offsetX: 6,
          formatter: v => v.toLocaleString("th-TH"),
          style: {
            fontSize: "10px",
            fontFamily: "Sarabun",
            colors: ["#1e293b"]
          }
        },
        tooltip: { y: { formatter: v => v.toLocaleString("th-TH") + " คน" } },
        grid: {
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: false } }
        }
      };
    },
    currDonutSeries() {
      return [this.currVcTotal, this.currHvcTotal];
    },
    currDonutOpts() {
      return {
        chart: {
          type: "donut",
          fontFamily: "Sarabun,sans-serif",
          animations: { speed: 500 }
        },
        labels: ["ปวช.", "ปวส."],
        colors: ["#1976d2", "#7b1fa2"],
        legend: { position: "bottom", fontSize: "13px" },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "รวม",
                  fontFamily: "Sarabun",
                  formatter: w =>
                    w.globals.seriesTotals
                      .reduce((a, b) => a + b, 0)
                      .toLocaleString("th-TH")
                }
              }
            }
          }
        },
        tooltip: { y: { formatter: v => v.toLocaleString("th-TH") + " คน" } }
      };
    }
  },

  // ═══════════════════════════ WATCHERS ═══════════════════════════
  watch: {
    filteredData() {
      this.refreshMapColors();
    },
    mapMode() {
      this.refreshMapColors();
    },
    activeTab(v) {
      if (v === 1 && !this.currJoinRows.length) this.loadCurriculumJoin();
    }
  },

  // ═══════════════════════════ LIFECYCLE ═══════════════════════════
  async mounted() {
    this.buildProvinceMap();
    await Promise.all([this.loadColleges(), this.loadCurriculum()]);
    await this.fetchData(this.searchYear, null);
    this.initStudentRangeMax();
    this.$nextTick(() => this.initLeafletMap());
  },
  beforeDestroy() {
    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }
  },

  // ═══════════════════════════ METHODS ═══════════════════════════
  methods: {
    // ─── Format / util ───
    formatNumber(n) {
      if (n === null || n === undefined || n === "") return "0";
      return Number(n).toLocaleString("th-TH");
    },
    truncate(s, len) {
      if (!s) return "";
      return s.length > len ? s.slice(0, len) + "…" : s;
    },
    showSnack(color, icon, text) {
      this.snack = { show: true, color, icon, text };
    },
    regionColor(region) {
      const c = {
        ภาคเหนือ: "#0288d1",
        ภาคกลาง: "#388e3c",
        "ภาคตะวันออก กรุงเทพและปริมณฑล": "#f57c00",
        ภาคตะวันออกเฉียงเหนือ: "#7b1fa2",
        ภาคใต้: "#c62828"
      };
      return c[region] || "#546e7a";
    },
    async swalLoading() {
      if (typeof Swal !== "undefined") {
        Swal.fire({
          title: "กำลังดำเนินการ...",
          html: "กรุณารอสักครู่",
          timerProgressBar: true,
          didOpen: () => Swal.showLoading(),
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false
        });
      }
    },

    // ─── curriculum helpers ───
    getSubjectTypeName(id) {
      const f = this.curriculumList.find(
        c => String(c.subjectTypeID) === String(id)
      );
      return f ? f.subjectTypeNameTh : String(id);
    },
    getMajorName(id) {
      const f = this.curriculumList.find(c => String(c.majorID) === String(id));
      return f ? f.majorNameTh : String(id);
    },

    // ─── Risk helpers ───
    riskLabel(total) {
      if (total === 0) return "ไม่มีผู้เรียน";
      if (total <= 10) return "วิกฤต";
      if (total <= 30) return "ต่ำมาก";
      if (total <= 100) return "ต่ำ";
      if (total <= 300) return "ปานกลาง";
      return "สูง";
    },
    riskColor(total) {
      if (total === 0) return "#b71c1c";
      if (total <= 10) return "#c62828";
      if (total <= 30) return "#e53935";
      if (total <= 100) return "#ef5350";
      if (total <= 300) return "#fb8c00";
      return "#2e7d32";
    },
    riskClass(total) {
      if (total === 0) return "risk-critical";
      if (total <= 30) return "risk-high";
      if (total <= 100) return "risk-mid";
      return "";
    },
    rankBadgeCls(rank) {
      if (rank === 1) return "rank-gold";
      if (rank === 2) return "rank-silver";
      if (rank === 3) return "rank-bronze";
      return "";
    },

    // ─── API calls ───
    async loadColleges() {
      try {
        const r = await this.$http.post("college.php", { ApiKey: this.ApiKey });
        this.colleges = r.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    async loadCurriculum() {
      this.loadingCurriculum = true;
      try {
        const r = await this.$http.post("curriculum.php", {
          ApiKey: this.ApiKey
        });
        this.curriculumList = r.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingCurriculum = false;
      }
    },
    async fetchData(year, collegeCode) {
      this.loading = true;
      try {
        const payload = { ApiKey: this.ApiKey, rate_work_college_year: year };
        if (collegeCode) payload.rate_work_college_code = collegeCode;
        const [r1, r2] = await Promise.all([
          this.$http.post("rate_work_college_admin.php", {
            ...payload,
            action: "list"
          }),
          this.$http.post("rate_work_college_admin.php", {
            ...payload,
            action: "report"
          })
        ]);
        this.rate_work_colleges = r1.data || [];
        this.rate_work_collegesReport = r2.data || [];
        this.buildProvinceMap();
        this.initStudentRangeMax();
        this.$nextTick(() => this.refreshMapColors());
      } catch (e) {
        this.showSnack(
          "error",
          "mdi-alert-circle",
          "เกิดข้อผิดพลาดในการโหลดข้อมูล"
        );
      } finally {
        this.loading = false;
      }
    },

    // ─── NEW: load curriculum JOIN data (action: curriculum_join) ───
    async loadCurriculumJoin() {
      this.loadingCurrJoin = true;
      try {
        const r = await this.$http.post("rate_work_college_admin.php", {
          ApiKey: this.ApiKey,
          rate_work_college_year: this.searchYear,
          action: "curriculum_join" // ← PHP block ใหม่ INNER JOIN curriculum WHERE curriculumYear >= 2567
        });
        this.currJoinRows = r.data || [];
      } catch (e) {
        this.showSnack(
          "error",
          "mdi-alert-circle",
          "โหลดข้อมูลหลักสูตรไม่สำเร็จ"
        );
        this.currJoinRows = [];
      } finally {
        this.loadingCurrJoin = false;
      }
    },

    // ─── filter events ───
    async onYearChange() {
      this.collegeSearch = "";
      this.clearAdvancedFilters();
      this.currJoinRows = [];
      await this.fetchData(this.searchYear, null);
      if (this.activeTab === 1) await this.loadCurriculumJoin();
    },
    onSubjectTypeChange() {
      const valid = new Set(this.majorOptions.map(m => m.majorID));
      this.filterMajors = this.filterMajors.filter(id => valid.has(id));
      this.applyFilters();
    },
    onRegionChange() {
      this.filterProvince = [];
      this.applyFilters();
    },
    applyFilters() {
      if (this.collegeSearch)
        this.fetchData(this.searchYear, this.collegeSearch);
      else this.fetchData(this.searchYear, null);
    },
    clearAdvancedFilters() {
      this.filterLevels = [];
      this.filterRegion = null;
      this.filterProvince = [];
      this.filterSubjectTypes = [];
      this.filterMajors = [];
      this.filterStudentRange = [0, this.studentRangeMax];
    },
    toggleLevel(v) {
      const i = this.filterLevels.indexOf(v);
      if (i > -1) this.filterLevels.splice(i, 1);
      else this.filterLevels.push(v);
    },
    removeLevel(v) {
      this.filterLevels = this.filterLevels.filter(x => x !== v);
    },
    removeSubjectType(v) {
      this.filterSubjectTypes = this.filterSubjectTypes.filter(x => x !== v);
      this.onSubjectTypeChange();
    },
    removeMajor(v) {
      this.filterMajors = this.filterMajors.filter(x => x !== v);
    },
    removeProvince(v) {
      this.filterProvince = this.filterProvince.filter(x => x !== v);
      this.applyFilters();
    },

    initStudentRangeMax() {
      const vals = this.rate_work_colleges.map(i => {
        return (
          (parseInt(i.rate_work_field_study_lavel_1) || 0) +
          (parseInt(i.rate_work_field_study_lavel_2) || 0) +
          (parseInt(i.rate_work_field_study_lavel_3) || 0)
        );
      });
      const mx = vals.length ? Math.max(...vals) : 500;
      this.studentRangeMax = mx;
      this.studentRangeStep = Math.max(1, Math.round(mx / 100));
      this.filterStudentRange = [0, mx];
    },
    filterByProvince(prov) {
      if (!prov) return;
      const region =
        prov.region ||
        (PROVINCE_META.find(p => p.name === prov.name) || {}).region;
      if (region) {
        this.filterRegion = region;
        this.showAdvanced = true;
      }
      this.filterProvince = [prov.name];
      this.applyFilters();
    },

    // ─── dialogs ───
    openCsvDialog() {
      this.selectedFile = null;
      this.csvDialog = true;
    },
    openApiDialog() {
      this.apiYear = this.currentYear;
      this.apiSemester = "";
      this.apiDialog = true;
    },
    async submitCsv() {
      if (!this.selectedFile) {
        this.showSnack("warning", "mdi-alert", "กรุณาเลือกไฟล์");
        return;
      }
      await this.swalLoading();
      try {
        const fd = new FormData();
        fd.append("file", this.selectedFile);
        fd.append(
          "filename",
          `../HRvecfiles/${Date.now()}.rate_work_college.csv`
        );
        fd.append("ApiKey", this.ApiKey);
        const r = await this.$http.post("rate_work_collegecsv.php", fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        if (typeof Swal !== "undefined") Swal.close();
        if (r.data.status) {
          this.showSnack("success", "mdi-check-circle", "นำเข้าข้อมูลสำเร็จ");
          await this.fetchData(this.searchYear, null);
        } else this.showSnack("error", "mdi-alert-circle", "เกิดข้อผิดพลาด");
      } catch (e) {
        if (typeof Swal !== "undefined") Swal.close();
        this.showSnack("error", "mdi-alert-circle", "เกิดข้อผิดพลาด");
      } finally {
        this.csvDialog = false;
      }
    },
    async submitApi() {
      if (!this.apiYear || !this.apiSemester) {
        this.showSnack("warning", "mdi-alert", "กรุณากรอกข้อมูลให้ครบ");
        return;
      }
      await this.swalLoading();
      try {
        await this.$http.post("GetStudentCount.php", {
          ApiKey: this.ApiKey,
          AcademicYear: this.apiYear,
          Semester: this.apiSemester
        });
        if (typeof Swal !== "undefined") Swal.close();
        this.showSnack("success", "mdi-check-circle", "ดึงข้อมูลสำเร็จ");
        await this.fetchData(this.searchYear, null);
      } catch (e) {
        if (typeof Swal !== "undefined") Swal.close();
        this.showSnack("error", "mdi-alert-circle", "เกิดข้อผิดพลาด");
      } finally {
        this.apiDialog = false;
      }
    },

    // ─── Leaflet Map ───
    buildProvinceMap() {
      const map = {};
      this.rate_work_colleges.forEach(row => {
        const pid = String(row.province_ID || "");
        if (!pid) return;
        if (!map[pid])
          map[pid] = {
            students: 0,
            colleges: new Set(),
            name: row.province_name || "",
            region: row.region_name || ""
          };
        const l1 = parseInt(row.rate_work_field_study_lavel_1) || 0;
        const l2 = parseInt(row.rate_work_field_study_lavel_2) || 0;
        const l3 = parseInt(row.rate_work_field_study_lavel_3) || 0;
        map[pid].students += l1 + l2 + l3;
        map[pid].colleges.add(row.rate_work_college_code);
      });
      Object.keys(map).forEach(k => {
        map[k].colleges = map[k].colleges.size;
      });
      const hasReal = Object.values(map).some(v => v.students > 0);
      if (!hasReal) {
        PROVINCE_META.forEach(p => {
          const pid = String(parseInt(p.id) || 0);
          map[pid] = {
            students: Math.round(p.base * (0.85 + Math.random() * 0.3)),
            colleges: Math.max(1, Math.round(p.base / 9000)),
            name: p.name,
            region: p.region
          };
        });
      }
      this.provinceDataMap = map;
    },
    async initLeafletMap() {
      const el = this.$refs.thMap;
      if (!el || this.leafletMap) return;
      this.leafletMap = L.map(el, {
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: true,
        minZoom: 5,
        maxZoom: 10
      });
      this.leafletMap.fitBounds(
        [
          [5.5, 97.3],
          [20.5, 105.7]
        ],
        { padding: [8, 8] }
      );
      try {
        const res = await fetch(
          "https://raw.githubusercontent.com/apisit/thailand.json/master/thailand.json"
        );
        const raw = await res.json();
        this.geoData = raw;
        this.geoReady = true;
        this.renderGeoLayer();
      } catch (e) {
        console.error("GeoJSON load error:", e);
      }
    },
    renderGeoLayer() {
      if (!this.leafletMap || !this.geoData) return;
      if (this.leafletGeoLayer) {
        this.leafletGeoLayer.remove();
        this.leafletGeoLayer = null;
      }
      this.leafletGeoLayer = window.L.geoJSON(this.geoData, {
        style: f => this.geoStyle(f),
        onEachFeature: (f, layer) => this.bindLayerEvents(f, layer)
      }).addTo(this.leafletMap);
    },
    geoStyle(feature) {
      const pid = this.getPid(feature);
      const data = this.provinceDataMap[pid] || { students: 0, colleges: 0 };
      const val = this.mapMode === "students" ? data.students : data.colleges;
      const max = this.mapMax();
      const ratio = max > 0 ? Math.min(val / max, 1) : 0;
      return {
        fillColor: this.lerpColor("#dbeafe", "#1e3a8a", ratio),
        fillOpacity: 0.82,
        color: "#ffffff",
        weight: 0.7,
        opacity: 1
      };
    },
    mapMax() {
      const vals = Object.values(this.provinceDataMap).map(d =>
        this.mapMode === "students" ? d.students : d.colleges
      );
      return vals.length ? Math.max(...vals) : 1;
    },
    bindLayerEvents(feature, layer) {
      const el = this.$refs.mapWrap;
      layer.on({
        mousemove: e => {
          const pid = this.getPid(feature);
          const data = this.provinceDataMap[pid] || {
            students: 0,
            colleges: 0,
            name: "",
            region: ""
          };
          const pname = data.name || this.getFeatureName(feature);
          const max = Math.max(
            ...Object.values(this.provinceDataMap).map(d => d.students),
            1
          );
          const rect = el ? el.getBoundingClientRect() : { left: 0, top: 0 };
          this.mapTip = {
            show: true,
            x: e.originalEvent.clientX - rect.left + 14,
            y: e.originalEvent.clientY - rect.top - 85,
            province: pname,
            region: data.region || "",
            students: data.students,
            colleges: data.colleges,
            pct: max > 0 ? Math.round((data.students / max) * 100) : 0
          };
          layer.setStyle({ weight: 2, color: "#1976d2", fillOpacity: 0.95 });
        },
        mouseout: () => {
          this.mapTip.show = false;
          this.leafletGeoLayer && this.leafletGeoLayer.resetStyle(layer);
        },
        click: () => {
          const pid = this.getPid(feature);
          const data = this.provinceDataMap[pid] || {
            students: 0,
            colleges: 0
          };
          this.selectedProvince = {
            name: data.name || this.getFeatureName(feature),
            region: data.region || "",
            students: data.students,
            colleges: data.colleges
          };
        }
      });
    },
    getPid(feature) {
      const p = feature.properties;
      const rawId =
        p.id ||
        p.pro_code ||
        p.prov_code ||
        p.PROV_CODE ||
        (p.ADM1_PCODE || "").replace("TH", "") ||
        "";
      const num = parseInt(rawId) || 0;
      if (num > 0) return String(num);
      const enName = p.name || p.NAME_1 || p.province || p.PROVINCE || "";
      if (PROVINCE_EN_TO_ID[enName]) return String(PROVINCE_EN_TO_ID[enName]);
      const normalized = enName.trim();
      const found = Object.keys(PROVINCE_EN_TO_ID).find(
        k => k.toLowerCase() === normalized.toLowerCase()
      );
      return found ? String(PROVINCE_EN_TO_ID[found]) : "0";
    },
    getFeatureName(feature) {
      const p = feature.properties;
      return p.name || p.prov_nameTH || p.NAME_1 || p.province || "";
    },
    refreshMapColors() {
      if (!this.leafletGeoLayer) return;
      this.leafletGeoLayer.eachLayer(layer => {
        if (layer.feature) layer.setStyle(this.geoStyle(layer.feature));
      });
    },
    lerpColor(a, b, t) {
      const ah = parseInt(a.slice(1), 16),
        bh = parseInt(b.slice(1), 16);
      const ar = (ah >> 16) & 0xff,
        ag = (ah >> 8) & 0xff,
        ab = ah & 0xff;
      const br = (bh >> 16) & 0xff,
        bg = (bh >> 8) & 0xff,
        bb = bh & 0xff;
      const rr = Math.round(ar + (br - ar) * t),
        rg = Math.round(ag + (bg - ag) * t),
        rb = Math.round(ab + (bb - ab) * t);
      return `#${((1 << 24) + (rr << 16) + (rg << 8) + rb)
        .toString(16)
        .slice(1)}`;
    },
    mapZoomIn() {
      this.leafletMap.zoomIn();
    },
    mapZoomOut() {
      this.leafletMap.zoomOut();
    },
    mapFit() {
      this.leafletMap.fitBounds(
        [
          [5.5, 97.3],
          [20.5, 105.7]
        ],
        { padding: [8, 8] }
      );
    },

    // ─── Excel exports ───
    exportToExcel() {
      const s = this.statistics;
      const rows = this.filteredData.map(i => ({
        ปีการศึกษา: i.rate_work_college_year,
        วิทยาลัย: i.college_name,
        จังหวัด: i.province_name || "",
        ภาค: i.region_name || "",
        ประเภทวิชา: i.subject_typeName,
        ระดับชั้น:
          String(i.rate_work_college_id_class) === "20" ? "ปวช." : "ปวส.",
        สาขาวิชา: i.name_type_course,
        "ชั้นปีที่ 1": parseInt(i.rate_work_field_study_lavel_1) || 0,
        "ชั้นปีที่ 2": parseInt(i.rate_work_field_study_lavel_2) || 0,
        "ชั้นปีที่ 3":
          String(i.rate_work_college_id_class) === "20"
            ? parseInt(i.rate_work_field_study_lavel_3) || 0
            : 0,
        รวม: i.total_row
      }));
      rows.push({}, {}, {});
      rows.push({
        ปีการศึกษา: "สรุป",
        ประเภทวิชา: "ปวช.1",
        "ชั้นปีที่ 1": s.ch1
      });
      rows.push({
        ปีการศึกษา: "สรุป",
        ประเภทวิชา: "ปวช.2",
        "ชั้นปีที่ 2": s.ch2
      });
      rows.push({
        ปีการศึกษา: "สรุป",
        ประเภทวิชา: "ปวช.3",
        "ชั้นปีที่ 3": s.ch3,
        รวม: s.totalVC
      });
      rows.push({
        ปีการศึกษา: "สรุป",
        ประเภทวิชา: "ปวส.1",
        "ชั้นปีที่ 1": s.s1
      });
      rows.push({
        ปีการศึกษา: "สรุป",
        ประเภทวิชา: "ปวส.2",
        "ชั้นปีที่ 2": s.s2,
        รวม: s.totalHVC
      });
      rows.push({ ปีการศึกษา: "รวมทั้งหมด", รวม: s.total });
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [10, 36, 18, 22, 18, 8, 40, 10, 10, 10, 10, 12].map(w => ({
        wch: w
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ข้อมูลนักเรียน");
      XLSX.writeFile(wb, `ข้อมูลนักเรียน_${this.searchYear}.xlsx`);
      this.showSnack("success", "mdi-check-circle", "ส่งออก Excel สำเร็จ");
    },
    exportCurriculumExcel() {
      const rows = this.currAllRanked.map(r => ({
        อันดับ: r.rank,
        ระดับ: r.level === "20" ? "ปวช." : "ปวส.",
        สาขาวิชา: r.majorName,
        รหัสสาขา: r.majorCode,
        ประเภทวิชา: r.subjectTypeName,
        "ชั้นปีที่ 1": r.y1,
        "ชั้นปีที่ 2": r.y2,
        "ชั้นปีที่ 3": r.level === "20" ? r.y3 : 0,
        รวม: r.total,
        "วิทยาลัย (แห่ง)": r.colleges,
        "สัดส่วน (%)": r.pct,
        สถานะ: this.riskLabel(r.total)
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [8, 8, 36, 14, 20, 10, 10, 10, 10, 14, 12, 12].map(w => ({
        wch: w
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "อันดับสาขาวิชา");
      XLSX.writeFile(wb, `อันดับสาขาวิชา_2567plus_${this.searchYear}.xlsx`);
      this.showSnack("success", "mdi-check-circle", "ส่งออก Excel สำเร็จ");
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700;800&family=Noto+Serif+Thai:wght@700&display=swap");

.rwc-admin {
  font-family: "Sarabun", sans-serif;
  background: #f0f4f8;
  min-height: 100vh;
}

/* ── HEADER ── */
.header-bar {
  background: linear-gradient(135deg, #0d1b4b 0%, #1565c0 50%, #4a148c 100%);
  border-radius: 16px;
  padding: 20px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  box-shadow: 0 8px 32px rgba(21, 101, 192, 0.35);
  position: relative;
  overflow: hidden;
}
.header-bar::after {
  content: "";
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
.header-bar-left {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
}
.header-bar-right {
  display: flex;
  gap: 8px;
  align-items: center;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
}
.header-icon-wrap {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.header-title {
  font-family: "Noto Serif Thai", serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
}
.header-sub {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
  margin: 2px 0 0;
}
.header-stat-pill {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: white;
  font-size: 0.82rem;
  padding: 6px 14px;
  border-radius: 999px;
  display: flex;
  align-items: center;
}
.header-stat-pill--accent {
  background: rgba(255, 255, 255, 0.22);
  font-size: 0.88rem;
}
.header-action-btn {
  text-transform: none !important;
  border-radius: 8px !important;
  font-size: 0.78rem !important;
  font-family: "Sarabun", sans-serif !important;
}

/* ── TABS ── */
.main-tabs-wrap {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
}

/* ── KPI ── */
.kpi-card {
  background: white;
  border-radius: 14px;
  padding: 18px;
  border-top: 4px solid var(--c);
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s, box-shadow 0.2s;
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
}
.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.kpi-label {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 600;
}
.kpi-avatar {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.kpi-value {
  font-size: 1.8rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
}
.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 4px;
}

/* ── PANELS ── */
.panel {
  background: white;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
}
.panel.h-100 {
  height: 100%;
}
.panel-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 13px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e293b;
}

/* ── MAP ── */
.map-wrap {
  position: relative;
  height: 420px;
  background: #f8fafc;
}
.th-map {
  width: 100%;
  height: 100%;
  background: #f8fafc !important;
}
:deep(.leaflet-container) {
  background: #f8fafc !important;
  font-family: "Sarabun", sans-serif;
}
:deep(.leaflet-control-zoom) {
  display: none !important;
}
:deep(.leaflet-control-attribution) {
  display: none !important;
}
.map-ctrl {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 800;
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.13);
  overflow: hidden;
}
.map-ctrl-btn {
  width: 34px;
  height: 34px;
  background: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  transition: background 0.15s;
}
.map-ctrl-btn + .map-ctrl-btn {
  border-top: 1px solid #f1f5f9;
}
.map-ctrl-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}
.map-legend {
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 800;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 8px 12px;
  min-width: 120px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
.map-legend-title {
  font-size: 0.7rem;
  font-weight: 700;
  color: #475569;
  margin-bottom: 5px;
  text-align: center;
}
.map-legend-grad {
  height: 9px;
  border-radius: 5px;
  margin-bottom: 3px;
  background: linear-gradient(
    90deg,
    #dbeafe,
    #bfdbfe,
    #60a5fa,
    #2563eb,
    #1e3a8a
  );
}
.map-legend-lbl {
  display: flex;
  justify-content: space-between;
  font-size: 0.67rem;
  color: #64748b;
  font-weight: 500;
}
.map-toggle .v-btn {
  min-width: 56px !important;
  font-size: 0.72rem !important;
  font-family: "Sarabun" !important;
}
.map-selected-info {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 800;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  font-size: 0.82rem;
  font-weight: 600;
  color: #1e293b;
}
.map-selected-val {
  color: #1976d2;
  font-weight: 700;
}
.map-tip {
  position: absolute;
  z-index: 900;
  pointer-events: none;
  background: rgba(15, 23, 42, 0.93);
  backdrop-filter: blur(8px);
  border-radius: 10px;
  padding: 11px 14px;
  min-width: 160px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}
.map-tip-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #f1f5f9;
  margin-bottom: 2px;
}
.map-tip-region {
  font-size: 0.7rem;
  color: #94a3b8;
  margin-bottom: 7px;
}
.map-tip-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
}
.map-tip-row span {
  font-size: 0.75rem;
  color: #94a3b8;
}
.map-tip-row strong {
  font-size: 0.8rem;
  color: #bfdbfe;
}
.map-tip-bar {
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  margin-top: 7px;
}
.map-tip-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #60a5fa, #3b82f6);
  transition: width 0.3s;
}
.tt-enter-active,
.tt-leave-active {
  transition: opacity 0.15s;
}
.tt-enter,
.tt-leave-to {
  opacity: 0;
}

/* ── FILTER PANEL ── */
.filter-panel {
  overflow: visible;
}
.filter-body {
  padding: 12px 16px 16px;
}
.filter-group {
  margin-bottom: 12px;
}
.filter-group-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}
.filter-field >>> .v-input__slot {
  min-height: 36px !important;
}
.filter-field >>> .v-label {
  font-size: 0.82rem !important;
}
.adv-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 7px 10px;
  border-radius: 8px;
  background: #f8fafc;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  transition: background 0.15s;
}
.adv-toggle:hover {
  background: #f1f5f9;
}
.adv-body {
  padding-top: 10px;
}
.level-chips {
  display: flex;
  gap: 8px;
}
.level-chip {
  padding: 4px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  border: 2px solid transparent;
  background: #f1f5f9;
  color: #64748b;
  transition: all 0.15s;
}
.level-chip.chip-vc {
  border-color: #1976d2;
  color: #1976d2;
}
.level-chip.chip-hvc {
  border-color: #7b1fa2;
  color: #7b1fa2;
}
.level-chip.chip-vc.active {
  background: #1976d2;
  color: white;
  border-color: #1976d2;
}
.level-chip.chip-hvc.active {
  background: #7b1fa2;
  color: white;
  border-color: #7b1fa2;
}
.level-chip:hover:not(.active) {
  background: #e8eaf6;
}

/* ── SUMMARY TABLE ── */
.summary-mini {
  background: #f8fafc;
  border-radius: 10px;
  padding: 10px 12px;
}
.summary-mini-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}
.summary-tbl {
  width: 100%;
  border-collapse: collapse;
}
.summary-tbl td {
  font-size: 0.8rem;
  padding: 4px 6px;
  color: #334155;
}
.summary-tbl td:last-child {
  font-family: "Roboto Mono", monospace;
  font-weight: 600;
}
.sum-sep-row td {
  border-top: 1px solid #e2e8f0;
  padding-top: 7px;
}
.sum-total-row td {
  border-top: 2px solid #1976d2;
  padding-top: 7px;
  color: #1976d2;
  font-weight: 700;
}
.action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
}

/* ── DATA TABLE ── */
.search-field {
  max-width: 420px;
}
.rwc-table >>> th {
  background: #f8fafc !important;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  color: #334155 !important;
  white-space: nowrap;
}
.rwc-table >>> td {
  font-size: 0.82rem;
  padding: 10px 14px !important;
}
.rwc-table >>> tr:hover {
  background: #eff6ff !important;
}
.cell-college {
  line-height: 1.35;
}
.cell-province {
  display: block;
  font-size: 0.72rem;
  color: #94a3b8;
}
.cell-subject {
  font-size: 0.78rem;
  color: #64748b;
}
.cell-course {
  font-size: 0.8rem;
  cursor: default;
}
.cell-num {
  font-size: 0.82rem;
}
.mono {
  font-family: "Roboto Mono", monospace;
  font-weight: 500;
}

/* ── REGION TABLE ── */
.region-table-wrap {
  overflow-x: auto;
}
.region-tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.region-tbl thead tr {
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}
.region-tbl th {
  padding: 10px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}
.region-tbl td {
  padding: 11px 14px;
  border-bottom: 1px solid #f1f5f9;
}
.region-row {
  cursor: pointer;
  transition: background 0.15s;
}
.region-row:hover {
  background: #f0f9ff;
}
.region-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 7px;
  vertical-align: middle;
}
.region-bar-bg {
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  margin-bottom: 2px;
  overflow: hidden;
}
.region-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}
.region-pct {
  font-size: 0.68rem;
  color: #94a3b8;
  font-weight: 600;
}
.region-total td {
  border-top: 2px solid #1976d2;
  padding-top: 10px;
  font-weight: 700;
  color: #1e293b;
}

/* ── RANK BADGE ── */
.rank-badge {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 800;
  color: #64748b;
  background: #f1f5f9;
  margin: auto;
}
.rank-gold {
  background: #fbbf24;
  color: #78350f;
}
.rank-silver {
  background: #94a3b8;
  color: white;
}
.rank-bronze {
  background: #cd7f32;
  color: white;
}

/* ── PCT BAR ── */
.pct-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 100px;
}
.pct-bar-bg {
  flex: 1;
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}
.pct-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s;
}
.pct-label {
  font-size: 0.72rem;
  color: #94a3b8;
  font-weight: 600;
  min-width: 32px;
}

/* ── CURRICULUM RISK LIST ── */
.curr-risk-list {
  padding: 6px 0;
  max-height: 280px;
  overflow-y: auto;
}
.curr-risk-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: 1px solid #f8fafc;
  transition: background 0.12s;
}
.curr-risk-row:hover {
  background: #fff7f7;
}
.curr-risk-row.risk-critical {
  background: #fff5f5;
}
.curr-risk-row.risk-high {
  background: #fff8f8;
}
.curr-risk-rank {
  font-size: 0.72rem;
  font-weight: 800;
  color: #94a3b8;
  min-width: 22px;
  text-align: center;
}
.curr-risk-info {
  flex: 1;
  min-width: 0;
}
.curr-risk-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1e293b;
}

/* ── DIALOGS ── */
.dlg-card {
  border-radius: 14px !important;
  overflow: hidden;
}
.dlg-head {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  color: white;
  font-size: 1rem;
  font-weight: 700;
}
.dlg-head--primary {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}
.dlg-head--purple {
  background: linear-gradient(135deg, #4a148c, #7b1fa2);
}

/* ── RESPONSIVE ── */
@media (max-width: 960px) {
  .header-title {
    font-size: 1.15rem;
  }
  .kpi-value {
    font-size: 1.45rem;
  }
  .map-wrap {
    height: 320px;
  }
  .filter-panel {
    position: relative !important;
    top: 0 !important;
  }
}
</style>
